import {
  angleModeFor, visibleTargeted, displayEvaluation,
  availableStructuralTriggers,
  orderingReachability,
  buildCustomOrderTrigger,
  branchesUnreachableInRange, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount,
  checkOptionFormulaIssues,
  doesOrderingCustomOrderMatchCorrect,
  evaluateEquation,
  evaluateOrdering,
  findOrderingOverlappingFeedbackPairs,
  getEquationVariableNames,
  orderBranchIssue,
  parseCustomOrderTrigger,
  parseThreshold,
  resolveEffectiveOrder,
  resolveMatchedBranch,
  activeFeedbackForBranch, reconcileOrder,
  addOrderingVersion, applyOrderingVersion, branchSetsToCheck, removeOrderingVersion, versionRollKey, writeOrderingVersion, pickScenarioBranch, resolveScenarioOrder , unreviewedCount } from '@agavi/blocks';
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Image as ImageIcon, ImageOff, Images, ListOrdered, Minus, Trash2, Volume2, VolumeX, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { pictureName, useAnimatedAssetIds } from './animated-image';
import { useAnswerRefScope } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { type BlockIssue, duplicateKeyIgnoringBlank, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { ChipDivider, FeedbackCard, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { FormulaField, FormulaWithValues } from './formula-field';
import { checkOptionChipCollisions, stripHtmlForLabel, stripHtmlForTooltip } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { LibraryModal } from './library-modal';
import { fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { OrderingCircle, type OrderingCircleDirection } from './ordering-circle';
import { OrderingItemMediaContent, type OrderingItemMedia } from './ordering-item-media';
import { recallOptionSpaces, recallSpacesKey } from './recall-option-spaces';
import { RichTextEditor } from './rich-text-editor';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioFollower } from './use-scenario-follower';
import { useDelayedStickyError, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useVariableMechanismAllowed } from './use-variable-affordance';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, AnswerDisplayFrame, formulaToRichText, hasRollableVariables, htmlHasVariableChips, PreviewHtml, processVariableChipHTML, rollVariableValues, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';

import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block, TaskVariable } from '@/types/experience';
import type { GradingMode , AngleMode, ConditionalOrderBranch, FeedbackState, OrderingBlockData, OrderingFeedback, OrderVersion, ScenarioOrderBranch, DrivenFollower } from '@agavi/blocks';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';
import { catchAllTitle, ORDERING_SURVEY_CATCH_ALL, legendStatesFor, orderingChips, orderingCustomOrderSequence, orderingStateCondition } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { orderingTargetedTitleText } from '@/lib/block-state-labels';

// Prevents a narrow, separate animation glitch from `@dnd-kit/sortable`: whenever a row's INDEX
// within the sortable items array changes (e.g. a commit reorders `items`), the library derives a
// one-off transform between its rect just before and just after that change, including its own
// scaleX/scaleY term. `animateLayoutChanges: () => false` disables that path entirely. This is
// UNRELATED to the "row grows/shrinks to match whatever it's hovering over" bug that every
// DndContext below now fixes via a `<DragOverlay>` — that one lives in `@dnd-kit/core` itself
// (`adjustScale` unconditionally recomputes scaleX/scaleY from `over.rect` on every active-drag
// frame, regardless of `animateLayoutChanges`) and is fixed by making the in-place row stop
// reading core's scaled transform at all once a DragOverlay is present. See ordering-circle.tsx's
// own DragOverlay comment for the full root-cause writeup — same bug, same fix, applied here for
// each of this file's own drag lists (item rows, custom-order rows, feedback cards).
const disableAnimateLayoutChanges = () => false;/** Bridges "jump to this branch" from the live-test widget (the collapsed canvas card) to the
 * editor — identical contract to Dropdown/MCQ's own `requestBranchJump`: separate mounted
 * instances of this component, with no other channel to hand off a value like this. See mcq-
 * block.tsx's own doc comment for the two-path (already-open vs. not-yet-open) reasoning. Own
 * copy, same duplication convention every block using this feature already follows. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface OrderingBlockProps {
  block: Block;
  isPreview: boolean;
  /** Needed only for the per-item media picker (LibraryModal) — same props Image/Sound/MCQ pass
   * through unchanged. */
  experienceId: string;
  taskId?: string;
  /** Bumped once per click of Preview's single bottom Submit — see task-content.tsx's own doc
   * comment on the signal. Only meaningful while `isPreview`; unused otherwise. */
  previewSubmitSignal?: number;
  /** `opts` carries `normalization`, for a write the block makes on its own behalf — see the
   *  media self-heal below, and `BlockUpdateOptions`. */
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

type OrderingShape = 'vertical' | 'horizontal' | 'circular';
type OrderingTriggerKind = 'reversed' | 'adjacent_swap' | 'off_by_one' | 'custom';

interface OrderingItemDraft {
  id: string;
  text: string; // rich text HTML, same convention as MCQOption.text
  /** Plain formula string, used INSTEAD of `text` whenever `OrderingBlockDataLocal.itemsAsEquations`
   * is on — mirrors `MCQOption.formula` exactly. See `packages/blocks/src/ordering.ts`'s
   * `OrderingItem.formula` doc comment for the full toggle contract. */
  formula?: string;
  /** An image or audio clip shown to students INSTEAD of `text` — mirrors `MCQOption.media`
   * exactly, including the "text stays the internal Label everywhere but the actual render"
   * contract. See `packages/blocks/src/ordering.ts`'s `OrderingItem.media` doc comment. */
  media?: OrderingItemMedia;
}

// Editor-local shape — matches every other input block's convention of keeping raw/editable
// state distinct from @agavi/blocks' already-normalized OrderingBlockData.
interface OrderingBlockDataLocal extends DrivenFollower {
  question: string;
  shape: OrderingShape;
  items: OrderingItemDraft[];
  gradingMode: GradingMode;
  feedback: { states: FeedbackState[] };
  /** Only meaningful for `shape === 'circular'` — see OrderingBlockData.circularDirection's own
   * doc comment in `packages/blocks/src/ordering.ts` for why this is a pure rendering/arrangement
   * choice, never a grading one. */
  circularDirection: OrderingCircleDirection;
  /** Variable-driven branching — see `ConditionalOrderBranch`'s doc comment in
   * `packages/blocks/src/ordering.ts`. Every Ordering block with no branches authored evaluates
   * exactly as it did before this feature existed. */
  conditionalEquation?: string;
  conditionalBranches?: ConditionalOrderBranch[];
  /** See `OrderingBlockData.conditionalBranchesDraft` — retained while branching is switched off. */
  conditionalBranchesDraft?: { equation: string; branches: ConditionalOrderBranch[] };
  /** Following a scenario: the leader, and one answer setup per variant — see
   *  `ScenarioOrderBranch`. Mutually exclusive with `versions` (§2.2). */
  followsBlockId?: string;
  scenarioBranches?: ScenarioOrderBranch[];
  /** This block's question bank — see `versions.ts`. A version's answer key is an id SEQUENCE,
   *  because the whole answer here is the order. */
  versions?: OrderVersion[];
  /** When true, every item's `formula` (not `text`) is evaluated against resolved variables and
   * the RESULT is what's shown to the student — identical contract to
   * `DropdownBlockData.optionsAsEquations`/`MCQBlockData.optionsAsEquations`. See
   * `OrderingBlockData.itemsAsEquations`'s own doc comment in `packages/blocks/src/ordering.ts` for
   * why the canonical order is entirely untouched by this. */
  itemsAsEquations?: boolean;
  angleMode?: AngleMode;
  /** Per-field modes, keyed by `fieldId` — see `angleModeFor`. Must survive the parse or the
   *  toggle writes a mode nothing reads. */
  angleModes?: Record<string, AngleMode>;
}

const MIN_ITEMS = 2;
const MIN_CIRCULAR_ITEMS = 3;
// Unlike MIN_CIRCULAR_ITEMS (a validity floor — too few items and there's no meaningful order to
// grade), this is a pure rendering/usability ceiling: the ring's radius grows with item count to
// keep tiles from overlapping (see ordering-circle.tsx's minRadiusForCount), so at 16 items the
// ring is already ~870px across — past a handful of items past this cap it stops being a usable
// widget on an ordinary screen, cap confirmed against real numbers, not an arbitrary guess.
const MAX_CIRCULAR_ITEMS = 8;

/** A new item, for an event handler. NEVER call this during render — see DEFAULT_ITEMS. */
function makeItem(): OrderingItemDraft {
  return { id: crypto.randomUUID(), text: '' };
}

/** The starter items, with FIXED ids — see DEFAULT_OPTIONS in mcq-block for the full reasoning. */
const DEFAULT_ITEMS: readonly OrderingItemDraft[] = [
  { id: 'default-item-1', text: '' },
  { id: 'default-item-2', text: '' },
];

function initData(raw: Record<string, unknown>): OrderingBlockDataLocal {
  const rawItems = Array.isArray(raw.items) ? raw.items as { id?: string; text?: string; formula?: string; media?: OrderingItemDraft['media'] }[] : [];
  return {
    question: (raw.question as string) ?? '',
    shape: (['vertical', 'horizontal', 'circular'].includes(raw.shape as string) ? raw.shape : 'vertical') as OrderingShape,
    items: rawItems.length >= MIN_ITEMS
      // Positional fallback id for legacy items saved without one: deterministic, so an item's
      // identity survives a re-render. A random id here churned on every render.
      ? rawItems.map((i, idx) => ({ id: i.id ?? `item-${idx}`, text: i.text ?? '', formula: i.formula, media: i.media }))
      : DEFAULT_ITEMS.map(i => ({ ...i })),
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as GradingMode,
    feedback: (raw.feedback ?? { states: [] }) as OrderingBlockDataLocal['feedback'],
    circularDirection: (raw.circularDirection === 'counterclockwise' ? 'counterclockwise' : 'clockwise'),
    conditionalEquation: raw.conditionalEquation as string | undefined,
    conditionalBranches: raw.conditionalBranches as ConditionalOrderBranch[] | undefined,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as OrderingBlockDataLocal['conditionalBranchesDraft'],
    /* Built field by field, so a field omitted here simply does not exist downstream — the link
       was read as absent, the leader could not be found, and the pager and the variant name
       both silently rendered nothing. Same delete-by-omission `versionStructure` documents. */
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    scenarioBranches: raw.scenarioBranches as ScenarioOrderBranch[] | undefined,
    itemsAsEquations: (raw.itemsAsEquations as boolean) ?? false,
    /* Builds a NEW object field by field, so anything not named is dropped — which is how a bank
       comes to be created, written to Mongo and then silently gone. */
    versions: raw.versions as OrderVersion[] | undefined,
    angleMode: (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
  };
}

function orderingTriggerKind(trigger: string): OrderingTriggerKind {
  if (trigger === 'reversed' || trigger === 'adjacent_swap' || trigger === 'off_by_one') return trigger;
  return 'custom';
}

/** Human-readable label for a single item — its own text (or, when `itemsAsEquations` is on, its
 * raw unresolved formula string — same "shows the raw formula, not an evaluated number" reasoning
 * as MCQ's own `mcqOptionLabel`, since this label backs titles that persist across rolls), bracketed
 * when the item is a media item (mirrors `mcqOptionLabel`'s identical bracket convention: `text`
 * stays the internal Label everywhere, but a bracket is the only signal available in a plain-text
 * title/chip that this isn't real student-facing content), or the positional fallback ("Item 2")
 * when blank. A blank rich-text field serializes to `<p></p>`, not `''` — truthy on the raw HTML,
 * so the fallback must check the STRIPPED label instead, or a blank item's raw markup silently
 * wins over its positional fallback (found live: a custom-order trigger over 3 blank items showed
 * ", ," instead of "Item 1, Item 2, Item 3"). Deliberately NOT consulted by `orderingStateCondition`
 * — that function is always fed items whose `.text` has already been resolved to real display
 * content (evaluated formula result included), so it never passes `itemsAsEquations` here at all. */
/** Label, sequence and title all live in `block-state-labels` now, so Flow derives the same string
 *  this writes instead of reading whatever this last wrote into the state. */

/** Moved to `lib/block-condition-chips` — see that file's note. */

/** The mechanically-derived, non-editable title for a targeted feedback state. Structural kinds
 * (reversed/adjacent_swap/off_by_one) can only ever have one trigger each, so their existing
 * fixed kind label is already unique by construction. "Custom Order" is different: a teacher can
 * configure several distinct custom-order triggers, each a different specific permutation, so
 * the generic "Custom Order" kind name alone would falsely collide two genuinely different
 * conditions — the actual item sequence has to be embedded in the title to stay unique. */
const orderingTargetedTitle = orderingTargetedTitleText;

/** Re-derives every targeted feedback title against the current items — needed whenever an
 * item's text changes or items are reordered/removed, since a custom-order title embeds the
 * full item sequence and a blank item's label falls back to its position ("Item 2"). */
function resyncOrderingTargetedTitles(items: OrderingItemDraft[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string, itemsAsEquations?: boolean): FeedbackState[] {
  return states.map(s => s.type === 'targeted' ? { ...s, title: orderingTargetedTitle(s.trigger, items, t, itemsAsEquations) } : s);
}

// A media item has real, orderable student-facing content regardless of whether its Label (an
// internal identifier, not displayed content) is filled in — same "never blank" rule as MCQ's own
// optionIsBlank.
//
// `itemsAsEquations` checks `formula` OR `text`, deliberately not formula-only. `text` is left
// untouched by the toggle (see OrderingBlockDataLocal.itemsAsEquations' own doc comment) — a
// pre-existing item with real content, flipped into equations mode, still genuinely HAS content;
// it just hasn't had its NEW formula authored yet. Requiring `formula` alone made turning the
// toggle on retroactively look like every item's content had vanished — nothing was deleted, but
// `isSetupIncomplete` (below) would immediately go true, hiding the collapsed-card preview and
// disabling the Circular shape option, the instant the toggle flipped, before a teacher had any
// chance to start authoring formulas. The per-item `item-formula-blank` diagnostic (in
// orderingIssues) already covers "this specific item's formula still needs filling in" on its
// own — this function's job is coarser: "is there a real, distinct item here at all," which a
// still-present-but-formula-less `text` already answers. A genuinely fresh item added AFTER
// equations mode is already on (blank `text` AND blank `formula`, since there's no UI path to type
// text while `formulaMode` is active) is still correctly blank under this check either way.
function orderingItemIsBlank(item: OrderingItemDraft, itemsAsEquations?: boolean): boolean {
  if (item.media) return false;
  const textBlank = item.text.replace(/<[^>]+>/g, '').trim() === '';
  return itemsAsEquations ? (!item.formula?.trim() && textBlank) : textBlank;
}

function isSetupIncomplete(d: OrderingBlockDataLocal): boolean {
  const filled = d.items.filter(i => !orderingItemIsBlank(i, d.itemsAsEquations));
  if (filled.length < MIN_ITEMS) return true;
  if (d.shape === 'circular' && filled.length < MIN_CIRCULAR_ITEMS) return true;
  return false;
}

function shuffledIds(ids: string[]): string[] {
  const arr = [...ids];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

/** Whether `candidate` is already the correct order — the same rotation-equivalence rule
 * evaluateOrdering itself uses for circular (any rotation of canonical counts), exact-order match
 * otherwise. A local, minimal check rather than reusing @agavi/blocks'
 * doesOrderingCustomOrderMatchCorrect here, since that takes the package's own OrderingItem[]
 * rather than this file's editor-local OrderingItemDraft[] and only `.id` is needed anyway. */
function isAlreadyCorrectOrder(candidate: string[], canonicalIds: string[], shape: OrderingShape): boolean {
  const n = canonicalIds.length;
  if (shape !== 'circular') return candidate.every((id, i) => id === canonicalIds[i]);
  for (let offset = 0; offset < n; offset++) {
    if (candidate.every((id, i) => id === canonicalIds[(i + offset) % n])) return true;
  }
  return false;
}

/** The starting arrangement for a fresh live-test/Preview attempt (entering test mode, or
 * Reset) — a genuine random shuffle, not a fixed transformation (a prior version always reversed
 * non-circular shapes and always kept circular at its canonical rotation — deterministic, and for
 * circular specifically, ALWAYS already-correct by rotation-equivalence, giving zero indication a
 * reorder was even needed). Reshuffles if the roll lands back on an already-correct arrangement
 * (exact match, or for circular, any rotation of canonical) — a non-canonical starting order is
 * what actually demonstrates the drag interaction; starting pre-solved would not. The retry cap is
 * generous or the loop would never need to run for a realistic item count/shape combination. */
function initialTestOrder(items: OrderingItemDraft[], shape: OrderingShape): string[] {
  const ids = items.map(i => i.id);
  if (ids.length < 2) return ids;
  let candidate = shuffledIds(ids);
  for (let attempt = 0; attempt < 20 && isAlreadyCorrectOrder(candidate, ids, shape); attempt++) {
    candidate = shuffledIds(ids);
  }
  return candidate;
}

/** Resolves what an item should actually DISPLAY as — identical contract to MCQ's own
 * `mcqOptionDisplayText`. When `itemsAsEquations` is on, the item's `formula` is evaluated and the
 * RESULT is what's shown (a plain formatted number, never the formula string itself); otherwise
 * falls through to `.text` exactly as before this feature existed. A media item is unaffected
 * either way — it never had `.text` shown to students in the first place (see `SortableItemRow`'s
 * Label input), so evaluating a formula here would be meaningless for it even if one existed
 * (mutually exclusive with media regardless). Blank/unevaluable stays blank, same "obviously
 * incomplete, never a placeholder" rule as everywhere else. */
function orderingItemDisplayText(
  item: OrderingItemDraft,
  itemsAsEquations: boolean | undefined,
  resolvedVariables: Record<string, number>,
  locale: string,
  angleMode: AngleMode,
): string {
  if (!itemsAsEquations || item.media) return item.text;
  if (!item.formula || !item.formula.trim()) return '';
  const v = evaluateEquation(item.formula, resolvedVariables, angleMode);
  return v === null ? '' : fmtLocale(v, locale);
}

// ─── Inoperable widget preview (shared by isPreview and the collapsed card) ───
// Renders the real shape-appropriate arrangement — a ring for circular, a row/column of chips
// otherwise — entirely inert (no drag, no keyboard), just "how will this look."

function OrderingWidgetPreview({ items, shape, circularDirection, itemsAsEquations }: { items: OrderingItemDraft[]; shape: OrderingShape; circularDirection: OrderingCircleDirection; itemsAsEquations?: boolean }) {
  if (shape === 'circular') {
    // `.text` is NOT blank in equations mode: it's deliberately preserved (not cleared) so
    // toggling equations mode back off restores the teacher's original rich text, exactly like
    // MCQ's own `text`/`formula` toggle contract. Left unhandled, that stale pre-equations text
    // would leak through here as actively MISLEADING content, not just a blank, less-informative
    // tile. Feed OrderingCircle a resolved item list instead, via its own `formulaChip` override —
    // a real styled `FormulaWithValues` chip, matching the standing "variables always render as a
    // bracketed chip in a display context" rule the vertical/horizontal branch below already
    // follows (this is a static preview with no rolled values to actually resolve against, so
    // every chip renders unresolved-braced, same as that branch's own `rolledValues={{}}`).
    const circularItems = itemsAsEquations
      ? items.map(i => (!i.media
          ? { ...i, formulaChip: <FormulaWithValues formula={i.formula ?? ''} rolledValues={{}} /> }
          : i))
      : items;
    return <OrderingCircle items={circularItems} disabled direction={circularDirection} />;
  }
  return (
    // Same column and the same centring as the live control — see `ANSWER_COLUMN_CLASS`.
    <div className={`${ANSWER_COLUMN_CLASS} flex gap-2 ${shape === 'horizontal' ? 'flex-row flex-wrap justify-center' : 'flex-col'}`}>
      {items.map(i => (
        <div key={i.id} aria-hidden='true' className='pointer-events-none flex items-center gap-2 rounded-lg border border-ws-border bg-ws-btn px-3 py-2 text-sm text-ws-text opacity-70'>
          <GripVertical className='h-4 w-4 shrink-0 text-ws-muted' />
          {itemsAsEquations ? (
            // Checked BEFORE `i.media` — equations mode suppresses media entirely, matching the
            // 2026-08-02 standing rule applied everywhere else this priority decision is made
            // (SortableItemRow, MCQ's SortableOptionRow, MCQ's own equivalent collapsed-card
            // preview). i.text is never populated in this mode (the item's real authored content
            // lives in i.formula instead) — show the raw, unresolved formula here rather than
            // i.text, matching the item-editing row's own preview. Not the evaluated result: this
            // collapsed canvas summary has no rolled variables to evaluate against outside
            // Preview/Test mode, and a value baked from today's roll would go stale the moment
            // variables re-roll. `{}` (not live rolledValues) forces every reference to a bare
            // styled chip, never an evaluated value — same convention MCQ's own static preview uses.
            <span className='min-w-0 text-sm text-ws-text'>
              <FormulaWithValues formula={i.formula ?? ''} rolledValues={{}} />
            </span>
          ) : i.media ? (
            <OrderingItemMediaContent media={i.media} constrained={shape === 'horizontal'} />
          ) : (
            <PreviewHtml
              // Same `<p>`-strip as OrderingRowContent's identical clamp branch — without it
              // `line-clamp` never engages, just a silent hard clip at 1 line. See that
              // component's own comment. Horizontal's flex-wrap row already stretches every item
              // in the same row to match its tallest sibling (e.g. an image), so a plain-text
              // item next to one is already exactly this tall — line-clamp-3 lets the text
              // actually use that height instead of sitting on one centered line inside it.
              html={shape === 'horizontal' ? i.text.replace(/<\/?p>/g, '').trim() : i.text}
              as='span'
              className={`min-w-0 ${shape === 'horizontal' ? 'max-w-48 line-clamp-3 break-words' : ''}`}
              title={shape === 'horizontal' ? (stripHtmlForTooltip(i.text).trim() || undefined) : undefined}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Live interactive answer widget (Preview, collapsed-card test, phone Test tab) ────────────
// Genuinely interactive, not the inert OrderingWidgetPreview above — the actual thing a student
// drags. Circular reuses OrderingCircle exactly as-is (it's already a fully interactive drag/
// keyboard widget whenever `disabled` is omitted — this was simply never done before, since its
// only prior caller always passed `disabled`); vertical/horizontal get a fresh, dnd-kit-based
// sortable list mirroring OrderingCustomOrderEditor's own read-only draggable-row shape, just fed
// from external `order`/`onChange` state instead of that editor's own trigger-scoped state.
function OrderingAnswerDisplay({
  question, items, shape, order, onChange, sensors, rolledValues, resetGeneration, children, circularDirection, scenario
}: {
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`. */
  scenario?: React.ReactNode;
  question: string;
  items: OrderingItemDraft[];
  shape: OrderingShape;
  order: string[];
  onChange: (order: string[]) => void;
  sensors: ReturnType<typeof useSensors>;
  rolledValues: Record<string, string>;
  circularDirection: OrderingCircleDirection;
  /** Bumped on Reset — forces OrderingCircle to remount and reseed from `order`, since it only
   * resyncs its own internal order state when the canonical item ID SET changes, never when just
   * the sequence does (see its own itemsKey effect). The vertical/horizontal branch below needs
   * no such trick — it's fully controlled by `order` already. */
  resetGeneration: number;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const byId = new Map(items.map(i => [i.id, i]));
  // Self-heals against item drift exactly like OrderingCustomOrderEditor does — this widget and
  // the block's own open editor can be two separately-mounted instances sharing the same
  // block.data (e.g. this collapsed card's test mode alongside an embedded editing panel), so
  // items can change shape out from under an in-progress test.
  // Self-heals against item drift — see `reconcileOrder`, which branching corrects, the custom
  // order editor and question versions all share.
  const reconciled = reconcileOrder(order, items);
  const [activeId, setActiveId] = useState<string | null>(null);

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(String(active.id));
  }
  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const from = reconciled.indexOf(String(active.id));
    const to = reconciled.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(reconciled, from, to));
  }
  function handleDragCancel() {
    setActiveId(null);
  }

  return (
    <AnswerDisplayFrame question={question} rolledValues={rolledValues} scenario={scenario}>
      {shape === 'circular' ? (
        <OrderingCircle
          key={resetGeneration}
          items={reconciled.map(id => byId.get(id)).filter((i): i is OrderingItemDraft => !!i)}
          onChange={onChange}
          direction={circularDirection}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          accessibility={dndA11y(t, reconciled, 'item')}
    >
          <SortableContext items={reconciled} strategy={shape === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy}>
            {/* `justify-center` on the row, as MCQ's own has: without it a wrapping row packs
                to the LEFT inside a column that is itself centred, which is centred by the
                column and visibly not centred to the eye. The stack needs none — its rows
                already fill the column. */}
            <div className={shape === 'horizontal' ? 'flex flex-row flex-wrap justify-center gap-2' : 'flex flex-col gap-1.5'}>
              {reconciled.map(id => {
                const item = byId.get(id);
                return item ? <SortableCustomOrderRow key={id} id={id} text={item.text} media={item.media}
                  position={reconciled.indexOf(id) + 1} total={reconciled.length}
                  constrained={shape === 'horizontal'} /> : null;
              })}
            </div>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeId ? (() => {
              const activeItem = byId.get(activeId);
              return activeItem ? <OrderingRowDragPreview text={activeItem.text} media={activeItem.media} constrained={shape === 'horizontal'} /> : null;
            })() : null}
          </DragOverlay>
        </DndContext>
      )}
      {children}
    </AnswerDisplayFrame>
  );
}

// ─── Sortable item row (canonical order editor) ────────────────────────────────

interface SortableItemRowProps {
  item: OrderingItemDraft;
  itemCount: number;
  armedItemId: string | null;
  setArmedItemId: (id: string | null) => void;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  itemEditorsRef: { current: Map<string, Editor> };
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  /** When set, the content editor is a plain FormulaField (mathjs expression, richtext fully
   * disabled) instead of the usual RichTextEditor — see OrderingBlockDataLocal.itemsAsEquations'
   * doc comment. `onFormulaChange`/`formulaHasError`/`variables`/`rolledValues`/`angleMode` are
   * only ever consulted in this mode. Identical contract to MCQ's own `formulaMode`. */
  formulaMode?: boolean;
  onFormulaChange?: (id: string, formula: string) => void;
  formulaHasError?: boolean;
  /**
   * This row is indistinguishable from another one — identical content, or content that resolves
   * the same for some roll of the variables it carries. The panel NAMES the collision; this points
   * at which rows it means. Stays lit while the row has focus: a collision is a relationship
   * between two rows, and you need to see the other one while fixing this one.
   */
  hasDuplicate?: boolean;
  variables?: TaskVariable[];
  rolledValues?: Record<string, string>;
  angleMode?: AngleMode;
  experienceId: string;
  taskId: string;
  taskBlocks: Block[];
  onMediaChange: (id: string, media: OrderingItemDraft['media'], seedLabel?: string) => void;
}

function SortableItemRow({
  item, itemCount, armedItemId, setArmedItemId, onTextChange, onRemove, itemEditorsRef, activeEditorRef, setActiveEditor,
  formulaMode, onFormulaChange, formulaHasError, hasDuplicate, variables, rolledValues, angleMode,
  experienceId, taskId, taskBlocks, onMediaChange,
}: SortableItemRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, animateLayoutChanges: disableAnimateLayoutChanges });
  // Same rule as every other error marker: wait for the row to stop changing before the ring
  // first appears, then hold it until the collision is actually gone.
  const showDuplicate = useDelayedStickyError(!!hasDuplicate, item.formula ?? item.text);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  /** Two presses to drop a placed asset — see MCQ's own `mediaArmed`, which this matches. */
  const [mediaArmed, setMediaArmed] = useState(false);
  /** See MCQ's own `mediaFramed`. */
  const mediaFramed = !!item.media && !formulaMode;
  // Seeded from the cache — see `peekAsset`, and image-block for the why.
  const [cachedAsset, setCachedAsset] = useState<AssetSummary | null>(
    () => (item.media?.assetId ? peekAsset(item.media?.assetId) ?? null : null),
  );
  // The Label field is a plain input (never a rich-text editor — see its render below), so it
  // needs its own local focus target instead of itemEditorsRef's Map of tiptap Editors.
  const labelInputRef = useRef<HTMLInputElement>(null);
  // The input's OWN displayed value, seeded once from item.text (stripped of any legacy rich-
  // text markup a plain-text item might have carried before media was ever attached) and updated
  // only by the user's own keystrokes from then on — deliberately NOT re-derived from item.text
  // via stripHtmlForLabel on every render. That used to be the input's `value`, but
  // stripHtmlForLabel ends in `.trim()`, so the instant a space was the LAST character typed (which
  // it always is, right when you type it), the next render trimmed it back off before the
  // following keystroke could land — spaces could never survive mid-word, silently squashing
  // "hello world" into "helloworld". item.text itself is still updated on every keystroke via
  // onTextChange below, so persistence is unaffected.
  const [labelDraft, setLabelDraft] = useState(() => stripHtmlForLabel(item.text));

  // Lazily resolve the placed asset (fresh signed URLs + filename) whenever the item only has an
  // assetId persisted — same pattern as MCQ's own SortableOptionRow.
  useEffect(() => {
    if (!item.media?.assetId) { setCachedAsset(null); return; }
    if (cachedAsset?.id === item.media.assetId) return;
    let cancelled = false;
    assetsApi.get(item.media.assetId).then((a) => { if (!cancelled) setCachedAsset(a); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.media?.assetId]);

  function handleMediaSelect(asset: AssetSummary, chosen: { altText?: string; transcript?: string }) {
    const existingLabel = stripHtmlForLabel(item.text).trim();
    const seededLabel = existingLabel || asset.filename.replace(/\.[^.]+$/, '');
    onMediaChange(
      item.id,
      { kind: asset.kind, assetId: asset.id, animated: !!asset.animated, altText: chosen.altText, transcript: chosen.transcript },
      existingLabel ? undefined : seededLabel,
    );
    setCachedAsset(asset);
    setShowLibraryModal(false);
    // Re-seed the label draft now, at the exact moment this row transitions into media mode —
    // item.text may still hold rich-text markup from before media was attached (or be a no-op if
    // it's already plain from a prior edit). Seeded from the asset's own filename when there is no
    // label yet, matching MCQ: this field is never shown to students, so unlike an answer value a
    // default cannot mislead anyone — it only has to be distinguishable, and the teacher's own
    // filename already is.
    setLabelDraft(seededLabel);
    requestAnimationFrame(() => { labelInputRef.current?.focus(); });
  }

  return (
    <div
      data-field-id={item.id}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
        armedItemId === item.id ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
      }`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.ordering.dragItem')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>

      {/* Framed when it holds media — see MCQ's own `mediaFramed` for why a four-control column
          needs an edge and a single editor box does not. */}
      <div className={`flex min-w-0 flex-1 flex-col gap-1 ${
        mediaFramed ? 'rounded-lg border border-ws-border bg-ws-btn/40 p-2' : 'rounded'
      } ${showDuplicate ? 'ring-1 ring-ws-error-fg' : ''}`}>
        {formulaMode ? (
          // Checked BEFORE `item.media` — equations mode suppresses media entirely (2026-08-02
          // standing rule, applied identically to MCQ's own `SortableOptionRow`). The toggle
          // handler already strips `media` from every item the moment this mode turns on, so by
          // the time this branch can be reached for a non-blank-media item, that's stale props
          // from a render in flight, not a real steady state — this ordering is the belt to that
          // handler's suspenders, not the primary enforcement.
          <FormulaField
            fieldId={`items.${item.id}.formula`}
            value={item.formula ?? ''}
            onChange={(formula) => onFormulaChange?.(item.id, formula)}
            variables={variables ?? []}
            rolledValues={rolledValues ?? {}}
            ariaLabel={t('blocks.ordering.itemFormulaLabel')}
            placeholder={t('blocks.ordering.itemFormulaPlaceholder')}
            hideEval
            hasError={formulaHasError}
            angleMode={angleMode}
            onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
          />
        ) : item.media ? (
          <>
            {/* The part clearing actually takes — see MCQ's own copy. The label stays, so it is
                deliberately outside the paint. */}
            <div className={`flex flex-col gap-1 rounded p-1.5 ${mediaArmed ? ARMED_DELETE_PAINT : ''}`}>
            <div className='flex items-center gap-1.5'>
              <button
                type='button'
                onClick={() => setShowLibraryModal(true)}
                aria-label={t('blocks.ordering.swapMedia')}
                className='flex min-w-0 flex-1 items-center gap-2 rounded-md border border-ws-border bg-ws-btn px-2 py-1 text-left hover:bg-ws-hover'
              >
                {item.media.kind === 'image' ? (
                  cachedAsset?.urls.thumb ? (
                    <img src={cachedAsset.urls.thumb} alt='' className='h-7 w-7 shrink-0 rounded object-cover' />
                  ) : (
                    <ImageIcon className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />
                  )
                ) : (
                  <Volume2 className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />
                )}
                <span className='min-w-0 flex-1 truncate text-xs text-ws-text'>
                  {cachedAsset?.filename ?? t('blocks.ordering.itemMediaLoading')}
                </span>
              </button>
              {/* Arms, and names what it removes — see MCQ's own copy of this control. */}
              <IconButton
                type='button'
                variant='destructive'
                size='sm'
                isArmed={mediaArmed}
                aria-label={mediaArmed ? t('blocks.actions.confirmDelete') : t('blocks.ordering.removeMedia')}
                onClick={() => {
                  if (!mediaArmed) { setMediaArmed(true); return; }
                  setMediaArmed(false);
                  onMediaChange(item.id, undefined);
                  requestAnimationFrame(() => { itemEditorsRef.current.get(item.id)?.commands.focus(); });
                }}
                onPointerLeave={(e) => { if (e.pointerType !== 'touch' && mediaArmed) setMediaArmed(false); }}
                onBlur={() => setMediaArmed(false)}
                onKeyDown={(e) => { if (e.key === 'Escape' && mediaArmed) { e.stopPropagation(); setMediaArmed(false); } }}
              >
                {item.media.kind === 'image'
                  ? <ImageOff className='h-3.5 w-3.5' />
                  : <VolumeX className='h-3.5 w-3.5' />}
              </IconButton>
            </div>
            {/* Alt-text only — image items need it (required, see the error check in
                orderingIssues); audio deliberately has no transcript input here, same accepted
                limitation as MCQ's own media options (see that block's own doc comment). */}
            {item.media.kind === 'image' && (
              <TextField
                variant='prose'
                value={item.media.altText ?? ''}
                onChange={v => onMediaChange(item.id, { ...item.media!, altText: v })}
                ariaLabel={t('blocks.image.altText')}
                placeholder={t('blocks.image.altText')}
                onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                className='text-xs' />
            )}
            {/* Only when the image MOVES — alt text describes the first frame and the content is
                in what changes (WCAG 1.2.1). Between the alt text and the internal label: the two
                things a reader is given, then the one only the teacher sees. */}
            {item.media.kind === 'image' && cachedAsset?.animated && (
              <TextField
                variant='prose'
                value={item.media.motionDescription ?? ''}
                onChange={v => onMediaChange(item.id, { ...item.media!, motionDescription: v })}
                ariaLabel={t('blocks.image.motionDescription')}
                placeholder={t('blocks.image.motionDescription')}
                onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                className='text-xs' />
            )}
            </div>
            {/* The (always-required) label — internal identifier for feedback/Flow/reports, never
                shown to students once media is present. Same field as the plain-text case
                (`item.text`), just relabeled. Deliberately a PLAIN input, not a RichTextEditor —
                same reasoning as MCQ's own option Label (see that block's own doc comment on why
                a rich-text Label is a structural hazard, not just an inconsistency). */}
              <TextField
                variant='literal'
                inputRef={labelInputRef}
                value={labelDraft}
                onChange={v => { setLabelDraft(v); onTextChange(item.id, v); }}
                onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                ariaLabel={t('blocks.ordering.itemLabelPlaceholder')}
                placeholder={t('blocks.ordering.itemLabelPlaceholder')}
                className='w-full' />
          </>
        ) : (
          <div className='flex min-w-0 items-center gap-1.5'>
            <div className='min-w-0 flex-1'>
              <RichTextEditor fieldId={`${item.id}.text`}
                content={item.text}
                onChange={(html) => onTextChange(item.id, html)}
                placeholder={t('blocks.ordering.itemPlaceholder')}
                editorClassName='min-h-6'
                hideToolbar
                onEditorReady={(e) => { itemEditorsRef.current.set(item.id, e); }}
                onEditorFocus={() => {
                  const e = itemEditorsRef.current.get(item.id);
                  if (e) { activeEditorRef.current = e; setActiveEditor(e); }
                }}
              />
            </div>
            <IconButton
              type='button'
              size='sm'
              aria-label={t('blocks.ordering.insertMedia')}
              onClick={() => setShowLibraryModal(true)}
            >
              <Images className='h-3.5 w-3.5' />
            </IconButton>
          </div>
        )}
      </div>

      {showLibraryModal && (
        <LibraryModal
          assetKind='any'
          experienceId={experienceId}
          currentAssetId={item.media?.assetId ?? null}
          currentTaskId={taskId}
          currentTaskBlocks={taskBlocks}
          onSelect={handleMediaSelect}
          onClose={() => setShowLibraryModal(false)}
        />
      )}

      <IconButton
        type='button'
        variant='destructive'
        size='md'
        isArmed={armedItemId === item.id}
        disabled={itemCount <= MIN_ITEMS}
        aria-label={armedItemId === item.id ? t('blocks.ordering.confirmRemoveItem') : t('blocks.ordering.removeItem')}
        className='shrink-0'
        onClick={() => {
          if (armedItemId === item.id) { onRemove(item.id); setArmedItemId(null); }
          else { setArmedItemId(item.id); }
        }}
        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedItemId === item.id) setArmedItemId(null); }}
        onBlur={() => { if (armedItemId === item.id) setArmedItemId(null); }}
      >
        <Trash2 className='h-4 w-4' />
      </IconButton>
    </div>
  );
}

/** Floating clone rendered by the Answer Setup list's `<DragOverlay>` — a static plain-text/media
 * snapshot, deliberately NOT a second live `SortableItemRow` (which would mount its own
 * `RichTextEditor`/tiptap instance and register itself in `itemEditorsRef` under the same item id
 * as the real row, a genuine collision, not just wasted work). Same "row grows/shrinks toward
 * whatever it's hovering over" bug this file's other DndContexts fix via DragOverlay — see the
 * module-level comment above `disableAnimateLayoutChanges`. */
function OrderingItemDragPreview({ item, itemsAsEquations }: { item: OrderingItemDraft; itemsAsEquations?: boolean }) {
  return (
    <div className='flex min-w-0 items-center gap-2 rounded-lg border border-ws-primary bg-ws-surface px-2 py-1.5 shadow-lg' style={{ cursor: 'grabbing' }}>
      <GripVertical className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' />
      {item.media ? (
        <OrderingItemMediaContent media={item.media} constrained />
      ) : (
        <span className='min-w-0 flex-1 truncate text-sm text-ws-text'>
          {stripHtmlForLabel(itemsAsEquations && !item.media ? (item.formula ?? '') : item.text) || ' '}
        </span>
      )}
    </div>
  );
}

// ─── Custom-order distractor editor (read-only text, reorder-only) ────────────

interface SortableCustomOrderRowProps {
  id: string;
  text: string;
  media?: OrderingItemDraft['media'];
  /** Where this row sits, and how many there are. The grip is the row's only focusable control,
   *  so without these every row in the list announced the identical six words. */
  position: number;
  total: number;
  /** Vertical is scroll-height-constrained only — an oversized image or a long line of text
   * just makes its own row taller, page scrolls, nothing else is affected, so both stay at their
   * natural "as-is" size (text wraps normally, never truncated — nothing to hide). Horizontal is
   * geometry-constrained instead: an oversized image OR an untruncated long line of text breaks
   * the flex-wrap layout itself (the row grows to fit its content instead of wrapping, throwing
   * off how items wrap relative to each other) — both media and text get the same size cap. Text
   * still isn't losing information when truncated here: the full string remains this row's own
   * accessible name-equivalent via the native `title` tooltip below, and nothing about drag/drop
   * or evaluation ever reads the truncated *display* text, only the full underlying item text. */
  constrained?: boolean;
}

/** The media-or-text content of a custom-order row, with no drag affordance of its own — shared
 * between the real sortable row (grip + this) and its DragOverlay floating clone (just this,
 * no grip listeners since the clone isn't itself draggable). */
function OrderingRowContent({ text, media, constrained, ownerName }: { text: string; media?: OrderingItemDraft['media']; constrained?: boolean;
  /** Passed through so an audio item's Play button says which clip it plays. */
  ownerName?: string }) {
  return media ? (
    <OrderingItemMediaContent media={media} constrained={constrained} ownerName={ownerName} />
  ) : (
    <PreviewHtml
      // `constrained` (horizontal) caps WIDTH but no longer caps text to a single line — the row
      // itself is a flex child of the shape's flex-wrap container (default align-items: stretch),
      // so a plain-text row already grows to match a taller sibling (e.g. one with an image) in
      // the same wrapped line; line-clamp-3 lets the text actually use that height instead of
      // sitting on one centered line inside an otherwise-empty tall box. RichTextEditor always
      // wraps its doc in `<p>`, even single-line content, so the untouched HTML has a block-level
      // child inside this span — `line-clamp` (like `truncate`'s ellipsis before it) only applies
      // to overflow on the box it's set on, not a nested block's own overflow, so without
      // stripping the wrapper the browser just hard-clips the text with no "…" at all — silently
      // misleading, not merely unpolished. Same fix MCQ/Dropdown's own truncated option chips
      // already use for this exact bug.
      html={constrained ? text.replace(/<\/?p>/g, '').trim() : text}
      as='span'
      className={`min-w-0 flex-1 text-sm text-ws-text break-words ${constrained ? 'max-w-48 line-clamp-3' : ''}`}
      title={constrained ? (stripHtmlForTooltip(text).trim() || undefined) : undefined}
    />
  );
}

function SortableCustomOrderRow({ id, text, media, position, total, constrained }: SortableCustomOrderRowProps) {
  const { t } = useTranslation();
  /* Asked of the ASSET, because an item stores only an id — the same reason MCQ asks. One row, one
     asset, and the hook de-duplicates across rows by id. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => (media?.kind === 'image' ? [media.assetId] : []), [media?.kind, media?.assetId]),
  );
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, animateLayoutChanges: disableAnimateLayoutChanges });
  /**
   * This row, named — which it was not.
   *
   * The grip is the only focusable thing in a row, and it announced `blocks.ordering.dragItem` on
   * every one of them: a student tabbing a five-item list heard "Drag to reorder item, button"
   * five times, with nothing saying which item they were holding or where it had got to. Position
   * matters here in a way it does not on the circular shape — a linear order has a real first and
   * last, where a ring is rotation-equivalent and `OrderingCircle` names its neighbours instead.
   *
   * A media item has no text of its own, so it falls back to its position exactly as the circular
   * shape's `labelFor` does.
   */
  /**
   * A MEDIA item is named by its ALT TEXT, never by `text`.
   *
   * `text` is a media item's internal label — storage, so a recorded answer can be read back later
   * — and the comment below used to say a media item simply has no text of its own. It does:
   * `handleMediaSelect` seeds the label from the asset's filename. So the row announced the
   * teacher's filing name and never the alt text, which is the one thing written FOR a listener.
   * Measured on real data, an item read as "Planet Chilla the space mouse" while its alt text said
   * "test".
   *
   * Composed the same way an MCQ media option is, so the two blocks name a picture identically —
   * and an animation carries its motion text here too, for the reason `animatedAltText` records:
   * it is content, and a description is the first thing a reader's settings suppress.
   */
  const mediaName = media?.kind === 'image'
    ? pictureName(media.altText, media.motionDescription, animatedAssetIds.has(media.assetId), t)
    : '';
  const plain = media ? mediaName : stripHtmlForTooltip(text).replace(/\s+/g, ' ').trim();
  const itemName = plain || t('blocks.ordering.itemFallbackLabel', { number: position });
  /**
   * The ROW is the control, not the grip inside it.
   *
   * With the name on the grip, the item text was announced twice — once as the grip's own name and
   * again as the sibling content beside it. That is the same fault MCQ's options had, and it has
   * the same resolution: one node that both names the thing and contains it, so a reader announces
   * it once. A control's name IS its content as far as assistive technology is concerned.
   *
   * A `<div>` carrying dnd-kit's role rather than a `<button>`, for the reason MCQ's media option
   * documents: an audio item nests WaveformPlayer's own Play button, and HTML forbids a button
   * inside a button. The grip keeps the pointer affordance and is hidden from the reader, since it
   * is now decoration on a row that is itself draggable.
   */
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      aria-label={t('blocks.ordering.dragItemNamed', { item: itemName, position, total })}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex min-w-0 cursor-grab touch-none items-center gap-2 rounded-md border border-ws-border bg-ws-btn px-2 py-1.5 active:cursor-grabbing focus-visible:[outline:2px_auto] ${isDragging ? 'opacity-40' : ''}`}
    >
      <div aria-hidden='true' className='flex h-6 w-4 shrink-0 items-center justify-center text-ws-muted'>
        <GripVertical className='h-3.5 w-3.5' />
      </div>
      <OrderingRowContent text={text} media={media} constrained={constrained} ownerName={itemName} />
    </div>
  );
}

/** Floating clone rendered by a custom-order list's `<DragOverlay>` — visually matches the real
 * row (grip + content) but the grip has no drag listeners, since the clone itself never receives
 * pointer events (dnd-kit renders it with `pointer-events: none` internally, and it always
 * un-mounts on drop). See the module-level comment above `disableAnimateLayoutChanges` for why
 * this exists at all: without a DragOverlay, the real in-place row reads `@dnd-kit/core`'s own
 * scaled transform and visibly stretches to match whatever it's hovering over. */
function OrderingRowDragPreview({ text, media, constrained }: { text: string; media?: OrderingItemDraft['media']; constrained?: boolean }) {
  return (
    <div className='flex min-w-0 items-center gap-2 rounded-md border border-ws-primary bg-ws-surface px-2 py-1.5 shadow-lg' style={{ cursor: 'grabbing' }}>
      <div className='flex h-6 w-4 shrink-0 items-center justify-center text-ws-muted'>
        <GripVertical className='h-3.5 w-3.5' />
      </div>
      <OrderingRowContent text={text} media={media} constrained={constrained} />
    </div>
  );
}

function OrderingCustomOrderEditor({
  items, order, onChange, sensors, itemsAsEquations,
}: {
  items: OrderingItemDraft[];
  order: string[];
  onChange: (order: string[]) => void;
  sensors: ReturnType<typeof useSensors>;
  /** A formula item's `text` is deliberately blank in this mode (see `orderingItemLabel`'s own
   * doc comment) — falls back to `formula`'s raw plain text so a teacher building a Custom Order
   * distractor can actually tell which row is which while dragging, instead of every non-media row
   * rendering as an unlabeled grip handle. */
  itemsAsEquations?: boolean;
}) {
  const { t } = useTranslation();
  // Self-heals against drift between the canonical item set and a previously-saved custom
  // order (an item added/removed after this trigger was set up) — never renders a phantom row
  // for a deleted item, and never silently drops a newly-added one from the picture.
  // Self-heals against item drift — see `reconcileOrder`, which branching corrects, the custom
  // order editor and question versions all share.
  const reconciled = reconcileOrder(order, items);
  const [activeId, setActiveId] = useState<string | null>(null);

  function handleDragStart({ active }: DragStartEvent) {
    setActiveId(String(active.id));
  }
  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const from = reconciled.indexOf(String(active.id));
    const to = reconciled.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onChange(arrayMove(reconciled, from, to));
  }
  function handleDragCancel() {
    setActiveId(null);
  }

  const activeItem = activeId ? items.find(i => i.id === activeId) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      accessibility={dndA11y(t, reconciled, 'item')}
    >
      <SortableContext items={reconciled} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-1.5'>
          {reconciled.map(id => {
            const item = items.find(i => i.id === id);
            // `constrained` here too — this list sits inside the feedback panel's own narrow
            // column, not the full-width Answer Setup editor, so an un-compacted audio player
            // (its own padded box plus a full-size play button and waveform) overflows the
            // column the same way it used to overflow a circular tile before that fix.
            const displayText = item ? (itemsAsEquations && !item.media ? (item.formula ?? '') : item.text) : '';
            return <SortableCustomOrderRow key={id} id={id} text={displayText} media={item?.media}
              position={reconciled.indexOf(id) + 1} total={reconciled.length} constrained />;
          })}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <OrderingRowDragPreview
            text={itemsAsEquations && !activeItem.media ? (activeItem.formula ?? '') : activeItem.text}
            media={activeItem.media}
            constrained
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────

interface OrderingTriggerOption {
  value: OrderingTriggerKind;
  label: string;
}

// Dropdown menu, not a segmented button group — matches mcq-block.tsx's MCQTriggerDropdown
// (per-option feedback trigger picker) exactly: a listbox button that opens a popover list,
// full keyboard support (Arrow/Home/End/Escape), same structural/visual conventions.
//
// One real difference from MCQ's version: MCQ colors its dropdown button from the CURRENTLY
// SELECTED OPTION's own evaluation (each option there is intrinsically correct/incorrect/neutral,
// via its role). Ordering's trigger kinds (Reversed/Adjacent Swap/Off By One/Custom Order) have no
// such intrinsic correctness of their own — they're just different KINDS of wrong-order pattern,
// all sharing the one `evaluation` the teacher already sets via this same feedback state's own
// cycler button elsewhere in the card. So this locks color/icon to that single `evaluation` value
// instead, applied uniformly no matter which kind is currently selected.

function OrderingTriggerEditor({
  trigger, onChange, items, shape, sensors, t, evaluation, usedKinds, itemsAsEquations,
}: {
  trigger: string;
  onChange: (trigger: string) => void;
  items: OrderingItemDraft[];
  shape: OrderingShape;
  sensors: ReturnType<typeof useSensors>;
  t: (k: string, opts?: Record<string, unknown>) => string;
  evaluation: FeedbackState['evaluation'];
  /** Structural kinds already claimed by OTHER targeted states (never this state's own current
   * kind) — each of reversed/adjacent_swap/off_by_one names exactly one specific arrangement, so
   * a second state claiming the same kind would just fire identically/redundantly with the first,
   * never a legitimate distinct condition. 'custom' is exempt: a custom order is parameterized by
   * its own specific sequence, so multiple custom-order states are already a normal, supported
   * pattern, not a duplicate. */
  usedKinds: OrderingTriggerKind[];
  itemsAsEquations?: boolean;
}) {
  const kind = orderingTriggerKind(trigger);
  const allKinds: OrderingTriggerKind[] = [...availableStructuralTriggers(shape), 'custom'];
  const availableKinds = allKinds.filter(k => k === 'custom' || k === kind || !usedKinds.includes(k));

  const kindLabelKeys: Record<OrderingTriggerKind, string> = {
    reversed: 'triggerTypeReversed', adjacent_swap: 'triggerTypeAdjacentSwap',
    off_by_one: 'triggerTypeOffByOne', custom: 'triggerTypeCustomOrder',
  };

  const customOrder = parseCustomOrderTrigger(trigger) ?? items.map(i => i.id);
  const dropdownOptions: OrderingTriggerOption[] = availableKinds.map(k => ({ value: k, label: t(`blocks.ordering.${kindLabelKeys[k]}`) }));

  return (
    <div className='flex w-full min-w-0 flex-col gap-2'>
      {/* THE SHARED PICKER. Ordering is the card-driven pole: a structural kind like "adjacent
          swap" has no correctness of its own, so the tone and the symbol come from the CARD's
          evaluation, which the teacher cycles. Passing `evaluation` is what selects that. */}
      <TriggerDropdown
        value={kind}
        options={dropdownOptions.map(o => ({ value: o.value, label: o.label }))}
        onChange={(k) => onChange(k === 'custom' ? buildCustomOrderTrigger(customOrder) : (k as OrderingTriggerKind))}
        ariaLabel={t('blocks.ordering.triggerTypeLabel')}
        evaluation={evaluation}
      />
      {kind === 'custom' && (
        <div className='flex flex-col gap-1.5'>
          <OrderingCustomOrderEditor
            items={items}
            order={customOrder}
            onChange={(next) => onChange(buildCustomOrderTrigger(next))}
            sensors={sensors}
            itemsAsEquations={itemsAsEquations}
          />
        </div>
      )}
    </div>
  );
}

/** A custom-order trigger's summary chip used to show a hardcoded generic "Specific order" label
 * — Flow's own chip for the identical state (orderingTargetedTitle's "Custom Order: {{sequence}}")
 * already showed the real item sequence correctly, so this was a real specificity gap, not a
 * deliberate simplification. Now shows the actual sequence too, truncated the same way MCQ's own
 * trigger chip (MCQTriggerChip) truncates a long option preview — `max-w-48` + `shrink-0` on the
 * chip itself, `min-w-0 truncate` on the text span (a flex child needs `min-w-0` for `truncate`'s
 * `overflow:hidden` to actually engage instead of the child forcing the flex row to grow).
 *
 * Also now colored/iconed by `evaluation`, matching every other block's own correct/incorrect/
 * neutral chip styling (see firedFeedbackEvalClass) — same "lock to the cycler state" reasoning
 * as OrderingTriggerDropdown's own doc comment: this trigger kind has no intrinsic correctness of
 * its own, only whatever evaluation the teacher already set on this feedback state. */
function OrderingTriggerChip({ trigger, evaluation, items, t, itemsAsEquations }: {
  trigger: string; evaluation: FeedbackState['evaluation']; items: OrderingItemDraft[]; t: (k: string, opts?: Record<string, unknown>) => string;
  itemsAsEquations?: boolean;
}) {
  const kind = orderingTriggerKind(trigger);
  const chipClass = firedFeedbackEvalClass(evaluation);
  const icon = evaluation === 'incorrect'
    ? <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
    : evaluation === 'correct'
      ? <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
      : <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />;
  if (kind === 'custom') {
    return (
      // Not `shrink-0` — this chip sits beside FeedbackCard's own reserved warning-icon/chevron
      // slots in its collapsed row (a `flex-1` container, so THIS is what's meant to give up width
      // first). `shrink-0` here was fighting that: capped at max-w-48 but never willing to go
      // narrower, a long custom-order sequence at that cap plus the warning icon overflowed the row
      // and visually pushed the chevron off past the panel's edge — confirmed live. Now on the
      // shared TRIGGER_CHIP_CLASS, which carries the same `min-w-0` fix for every block.
      <span className={`${TRIGGER_CHIP_CLASS} ${chipClass}`}>
        {icon}
        <ChipDivider />
        <span className={TRIGGER_CHIP_TEXT_CLASS}>{orderingCustomOrderSequence(trigger, items, t, itemsAsEquations)}</span>
      </span>
    );
  }
  const labelKeys: Record<'reversed' | 'adjacent_swap' | 'off_by_one', string> = {
    reversed: 'triggerSummaryReversed', adjacent_swap: 'triggerSummaryAdjacentSwap', off_by_one: 'triggerSummaryOffByOne',
  };
  return (
    // The SHARED chip classes, like the custom-order branch above — this one was hand-rolled with
    // `shrink-0` and no wrapping, so "Two adjacent items swapped" held its full width, overran
    // FeedbackCard's reserved warning slot and covered the no-text warning beside it.
    <span className={`${TRIGGER_CHIP_CLASS} ${chipClass}`}>
      {icon}
      <ChipDivider />
      <span className={TRIGGER_CHIP_TEXT_CLASS}>{t(`blocks.ordering.${labelKeys[kind]}`)}</span>
    </span>
  );
}
// ─── Standardized issue reporting ──────────────────────────────────────────────
// Mirrors mcqIssues/dropdownIssues' shape exactly — one computation read by the collapsed
// card's own top-priority bar and the in-editor sticky warningBarSlot, so the two surfaces can
// never disagree about wording. Blank-field checks are tagged `incompletion` — an absence is a
// block you have not finished, not a block that is broken, so it waits for the completion
// boundary. Overlap/matches-correct checks are `defect`s: they describe a contradiction between
// two things the teacher actually authored, which no amount of further typing resolves.
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedOrderingData(
  rawD: OrderingBlockDataLocal,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): OrderingBlockDataLocal {
  const rawVersioned = applyOrderingVersion(rawD, versionId);
  return { ...rawVersioned, feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode, { defaults: { incorrectTitle: t('blocks.ordering.incorrectAnyOtherOrder') } }) };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `orderingIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function orderingBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return orderingIssues(buildNormalizedOrderingData(initData(block.data), ctx.t), ctx);
}

export function orderingIssues(d: OrderingBlockDataLocal, ctx: IssueContext): BlockIssue[] {
  const { t, variables, recalls = {} } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');
  const categoryAlternativeCorrectStates = t('blocks.areas.alternativeAnswerSetup');
  const questionBlank = !d.question.replace(/<\/?p>/g, '').trim();

  if (questionBlank) issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  // Fewer than MIN_ITEMS (or MIN_CIRCULAR_ITEMS for circular) filled items means there's no
  // complete canonical order to grade against at all — the same severity as MCQ's "all options
  // blank"/"no correct option", not merely cosmetic the way the old warning-tier bar treated it.
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  /**
   * §4.4 — a row copied by the projection is "copied, not decided". The box shows a count
   * beside the pager for the row you are on; this is the half that reaches the publication
   * gate, so a teacher who never pages to scenario 5 still learns it was never reviewed.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  if (d.gradingMode === 'graded' && isSetupIncomplete(d)) issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.ordering.barSetupIncomplete') });

  // MCQ has no per-option equivalent of this — a blank option there is invisible behind its own
  // `role`, so only the aggregate "every option blank" case is worth flagging. Ordering has no
  // such cover: a blank formula item renders as nothing, is visually indistinguishable from every
  // OTHER blank formula item (a real, not hypothetical, fairness collision — two blank drag tiles
  // are identical), and unlike `setup-incomplete` this can be true even when there are still
  // plenty of OTHER filled items, so it needs its own check rather than riding on that one.
  if (d.itemsAsEquations && d.items.some(i => !i.media && !i.formula?.trim())) {
    issues.push({ key: 'item-formula-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.ordering.itemFormulaBlankWarning') });
  }

  // Exact-duplicate item text is the same fairness hazard MCQ/Dropdown/Keyword/FITB already flag
  // for their own answer content — a student can't tell two identically-worded items apart, so
  // there's no way to know which physical item belongs in which position. A `defect`, not an
  // incompletion: two identical items are a contradiction, not a blank waiting to be filled
  // (this matches every other block's own duplicate check), and deliberately exact
  // raw-HTML equality, not case/formatting-normalized — two items that only look the same after a
  // formatting difference are not flagged, only items whose stored content is byte-for-byte
  // identical.
  // Media items are excluded here — their `text` is the internal label (feedback/Flow/reports),
  // not student-facing content, so two media items sharing a label is meaningless to flag; what
  // actually matters for them is a shared assetId, checked separately below. Skipped entirely in
  // itemsAsEquations mode — same reasoning as MCQ's own mcqIssues: `text` is never populated
  // there (a teacher only ever fills in the FormulaField), so this would just flag every item as
  // an identical duplicate of every other blank one; the formula collision check below already
  // covers the real fairness concern with the actual evaluated value, not a raw-string comparison.
  if (!d.itemsAsEquations) {
    for (const group of findDuplicateGroups(d.items.filter(i => !i.media), i => duplicateKeyIgnoringBlank(i.text))) {
      issues.push({
        key: `duplicate-${group.map(i => i.id).join('-')}`,
        fields: group.map(i => i.id),
        severity: 'error',
        kind: 'defect',
        category: categoryAnswerSetup,
        detail: t('blocks.ordering.duplicateItemWarning', { text: stripHtmlForLabel(group[0]!.text) }),
      });
    }
  }

  // Two items pointing at the exact same asset — a student can't tell which physical item
  // occupies which position, exactly as much a fairness problem here as duplicate text is.
  for (const group of findDuplicateGroups(d.items.filter(i => i.media), i => i.media!.assetId)) {
    issues.push({
      key: `duplicate-media-${group.map(i => i.id).join('-')}`,
      fields: group.map(i => i.id),
      severity: 'error',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t('blocks.ordering.duplicateMediaWarning'),
    });
  }

  // A blank Label on a media item silently breaks feedback/Flow/report text (they'd all read
  // blank for this item) — a real error, not just a cosmetic gap, hence severity `error`. Still an
  // `incompletion` though: severity is how loudly it's drawn, `kind` is when it's worth saying, and
  // a label you have not typed yet is an absence however badly it ends up mattering.
  const itemsMissingLabel = d.items.filter(i => i.media && !i.text.replace(/<\/?p>/g, '').trim());
  if (itemsMissingLabel.length > 0) {
    issues.push({ key: 'media-label-blank', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemMediaLabelBlankWarning') });
  }

  // Missing alt-text on an image item is a real accessibility error — without it a screen-reader
  // user has no way to know what the item even is. Audio deliberately has no transcript-required
  // equivalent, same accepted limitation as MCQ's own media options.
  const imageItemsMissingAlt = d.items.filter(i => i.media?.kind === 'image' && !i.media.altText?.trim());
  /* Same obligation as the alt text above, and the same `kind` as it -- matching the neighbour
     rather than the Image block, which calls its own an incompletion. */
  const animatedItemsMissingMotion = d.items.filter(
    i => i.media?.kind === 'image' && i.media.animated === true && !i.media.motionDescription?.trim(),
  );
  if (imageItemsMissingAlt.length > 0) {
    issues.push({ key: 'media-alt-text-missing', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemMediaAltTextRequired') });
  }
  if (animatedItemsMissingMotion.length > 0) {
    issues.push({ key: 'media-motion-description-missing', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemMediaMotionDescriptionRequired') });
  }

  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  const hasMissingFeedbackText = hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) ||
    !!d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states));
  if (hasMissingFeedbackText) issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });

  // Previously only visible per-card while the editor was open (SortableFeedbackCard's own
  // `warning` prop) — aggregated here too so the collapsed card and any future test-panel
  // diagnostics also surface it, not just an open editor. One issue total per kind, not one per
  // colliding pair/state, matching the just-shipped MCQ/Dropdown chip-collision collapse.
  if (d.gradingMode === 'graded' && !isSetupIncomplete(d)) {
    const targetedStates = d.feedback.states.filter(s => s.type === 'targeted');
    const allTriggers = targetedStates.map(s => s.trigger);
    const overlappingPairs = findOrderingOverlappingFeedbackPairs(allTriggers, d.items, d.shape);
    if (overlappingPairs.length > 0) issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.ordering.feedbackOverlapWarning') });

    const anyMatchesCorrect = targetedStates.some(s => {
      const customOrder = parseCustomOrderTrigger(s.trigger);
      return customOrder && doesOrderingCustomOrderMatchCorrect(customOrder, d.items, d.shape);
    });
    // Circular gets its own wording — a custom order that doesn't look identical to the
    // canonical sequence can still "match" there, since doesOrderingCustomOrderMatchCorrect
    // treats every rotation as correct for circular shape (rotation-equivalence). Without calling
    // that out explicitly, this reads as a false positive rather than the intended design.
    const matchesCorrectKey = d.shape === 'circular' ? 'feedbackOverlapsCorrectWarningCircular' : 'feedbackOverlapsCorrectWarning';
    if (anyMatchesCorrect) issues.push({ key: 'feedback-matches-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t(`blocks.ordering.${matchesCorrectKey}`) });
  }

  // Everything below requires an explicit action (turning on branching, typing an equation,
  // authoring a branch's own order) — see mcqIssues/dropdownIssues' own comment for why
  // none of this needs the grace period, and why it's NOT gated on `isSetupIncomplete` either.
  /* PER VERSION: these checks read version-resolved data, so a banked block was only ever
     validated for the version being displayed. See `branchSetsToCheck`. */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalOrderBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') })) });
        }
        if (overlapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        // The Ordering analog of MCQ's none-correct/multiple-correct: a branch's own `order` isn't
        // a valid permutation of the CURRENT item id set (e.g. an item was added/removed since this
        // branch was set up) — see orderBranchIssue's own doc comment in packages/blocks.
        if (orderBranchIssue(b, d.items) === 'invalid-order') {
          issues.push({ key: vkey(`branch-${b.id}-invalid-order`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.ordering.diagnosticBranchInvalidOrder', { number: i + 1 })) });
        }
        if (b.operator === 'between') {
          const lo = parseThreshold(b.threshold);
          const hi = parseThreshold(b.thresholdHigh);
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }

        // Ordering-specific, no MCQ/Dropdown equivalent (they have no per-branch analog of these two
        // checks at all — see the plan's own callout): the existing top-level overlap/matches-correct
        // checks above run against the BASE `d.items` order, but once branching is on, what's
        // actually correct under THIS branch is its own `order` — a branch's targeted custom-order
        // feedback needs validating against that, not the base sequence. Skipped when the branch's
        // own order is itself invalid (already flagged above) — there's no coherent canonical
        // sequence to check overlap/matches-correct against in that case.
        if (orderBranchIssue(b, d.items) === null && !isSetupIncomplete(d)) {
          const branchItemsInOrder = b.order.map(id => d.items.find(item => item.id === id)!);
          const branchTargeted = b.feedback.states.filter(s => s.type === 'targeted');
          const branchOverlapPairs = findOrderingOverlappingFeedbackPairs(branchTargeted.map(s => s.trigger), branchItemsInOrder, d.shape);
          if (branchOverlapPairs.length > 0) {
            issues.push({ key: vkey(`branch-${b.id}-feedback-overlap`), severity: 'warning', kind: 'defect', category: categoryFeedback, detail: vlabel(t('blocks.ordering.branchFeedbackOverlapWarning', { number: i + 1 })) });
          }
          const branchMatchesCorrect = branchTargeted.some(s => {
            const customOrder = parseCustomOrderTrigger(s.trigger);
            return customOrder && doesOrderingCustomOrderMatchCorrect(customOrder, branchItemsInOrder, d.shape);
          });
          if (branchMatchesCorrect) {
            const branchMatchesCorrectKey = d.shape === 'circular' ? 'branchFeedbackMatchesCorrectWarningCircular' : 'branchFeedbackMatchesCorrectWarning';
            issues.push({ key: vkey(`branch-${b.id}-feedback-matches-correct`), severity: 'warning', kind: 'defect', category: categoryFeedback, detail: vlabel(t(`blocks.ordering.${branchMatchesCorrectKey}`, { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Two DIFFERENT items resolving to identical displayed value is exactly as much a fairness
  // problem here (a student can't tell which physical item occupies which position) as it is for
  // MCQ's own options — same brute-force collision check, same one-collapsed-issue convention.
  // itemsAsEquations mode shifts this to the full-formula unevaluable/collision check instead of
  // the plain-chip one — same split as MCQ's own mcqIssues.
  const chipVars = variables;
  if (d.itemsAsEquations) {
    const { issues: formulaIssues } = checkOptionFormulaIssues(d.items, chipVars, id => angleModeFor(d, `items.${id}.formula`));
    for (const issue of formulaIssues) {
      if (issue.kind === 'unevaluable') {
        issues.push({ key: `item-${issue.optionIds[0]}-unevaluable`, fields: issue.optionIds, severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemFormulaUnevaluable') });
      }
    }
    // One issue total, not one per colliding pair — same reasoning as MCQ/Dropdown's own collapse.
    // Reuses the plain-chip mode's own collision message below — "could show the same text" reads
    // correctly for either case, same shared-message convention MCQ's own equation-mode collision
    // check uses against its plain-chip counterpart. `error`, not `warning` — a FULL-FORMULA
    // collision means two items are guaranteed identical on some roll (a real, certain fairness
    // hazard, same severity as the unevaluable check right above), unlike the plain-chip mode's own
    // collision below, which is only a coincidence of the specific ranges chosen. Matches MCQ's own
    // `option-collision` (mcq-block.tsx)/Dropdown's own equivalent, both `error` for exactly this
    // same distinction — this was a genuine severity-tier mismatch, not a deliberate difference.
    if (formulaIssues.some(i => i.kind === 'collision')) {
      issues.push({ key: 'item-formula-collision', fields: formulaIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemChipCollision') });
    }
  } else {
    const { issues: chipIssues } = checkOptionChipCollisions(d.items, chipVars, recalls);
    if (chipIssues.length > 0) {
    // ERROR, matching the equation-mode collision above, because it IS the same check: both
    // enumerate every combination of the declared variable domains and flag a pair that matches in
    // any one of them. Neither proves the two are always equal. This was a warning on the theory
    // that a chip collision is "a coincidence of the ranges chosen" while a formula collision is
    // mathematical identity — but `checkOptionFormulaIssues` samples the same finite domain and
    // reports the same kind of coincidence, so the distinction was not real.
    //
    // It had the severity backwards, too. In equation mode a teacher can read two formulas side by
    // side and reason about whether they collide; with chips in plain text the collision is
    // invisible without enumerating rolls mentally. The harder case to spot was the quieter one.
      issues.push({ key: 'item-chip-collision', fields: chipIssues.flatMap(i => i.optionIds), severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.ordering.itemChipCollision') });
    }
  }

  return issues;
}

// ─── Live test data + fired-feedback rendering ─────────────────────────────────

function firedFeedbackEvalClass(evaluation: FeedbackState['evaluation']): string {
  return chipToneClass(evaluation);
}
// Fired-feedback display — Preview and the live test widget both show this identically once an
// answer has been submitted. Same shape as MCQ's own renderMCQFiredFeedback (kept as a separate
// copy per that function's own doc comment on why — no shared non-block-scoped home for a
// two-or-three-block-only helper this small): `default` states (No Answer/Correct/Incorrect/
// any_answer/any_response) only ever fire alone (evaluateOrdering returns `correct` in total
// isolation, terminally), so they keep a one-box-per-state look; `targeted` states are the ones
// that can fire in parallel (a submitted order can match more than one structural/custom
// trigger at once) and are never `evaluation: 'correct'` by construction, so clustering them into
// just an incorrect and a neutral bucket is exhaustive.
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderOrderingFiredFeedback(testResult: FeedbackState[] | null, rolledValues: Record<string, string>): React.ReactNode {
  return <FiredFeedback states={testResult} rolledValues={rolledValues} />;
}

interface OrderingTestData {
  testResult: FeedbackState[] | null;
  resolvedQuestion: string;
  /** `d.items`, with each item's displayed `text` already resolved for this test roll — plain
   * variable-chip substitution normally, or (when `itemsAsEquations`) the item's `formula`
   * evaluated to a real number via `orderingItemDisplayText`. Fed straight into
   * `OrderingAnswerDisplay`'s own `items` prop by every caller, exactly like MCQ's own
   * `resolvedD.options` — the interactive widget itself has no other path to a formula's actual
   * evaluated value; it only ever renders whatever `.text` it's handed. */
  resolvedItems: OrderingItemDraft[];
  testChips: TestBarChip[];
  hasChips: boolean;
  hasRollable: boolean;
  matchedBranch: ConditionalOrderBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
}

/** Computes everything the live test widget/test bar needs — same role as MCQ/Dropdown's own
 * computeMCQTestData/computeDropdownTestData, called fresh on every render of whichever surface
 * needs it (collapsed card, phone Test tab, Preview) rather than memoized, so the three can never
 * disagree. Structurally thinner than MCQ's version in one respect (no per-option correct/
 * incorrect fan-out — there's exactly one canonical sequence, and no discrete answer domain for
 * Default Incorrect to enumerate against, so every targeted state is always shown as-is), but now
 * mirrors it for branch resolution (see ConditionalOrderBranch). */
function computeOrderingTestData(
  d: OrderingBlockDataLocal,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submittedOrder: string[],
  touched: boolean,
  hasSubmitted: boolean,
  locale: string,
  /** The leader's variant in force, when this block follows one — see `evaluateOrdering`. */
  scenarioId?: string,
): OrderingTestData {
  const resolvedQuestion = processVariableChipHTML(d.question, rolledValues);
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  // Branches are only ever consulted in graded mode — see evaluateOrdering's own gate. Otherwise a
  // leftover branch from a mode switch would still resolve here even though the real evaluation
  // now ignores it, showing a "Testing Condition" banner for a mode that's actually running on the
  // plain top-level feedback.
  /* A FOLLOWER resolves against the scenario in force instead of an equation — the same split
     `evaluateOrdering` makes internally, mirrored because this builds what the teacher SEES and
     the two must never disagree about which answer key is live. */
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch<ScenarioOrderBranch>(d.scenarioBranches!, scenarioId) : null;
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  const activeFeedbackForDisplay = scenarioRow ? scenarioRow.feedback : matchedBranch ? matchedBranch.feedback : d.feedback;
  const effectiveOrder = d.gradingMode !== 'graded'
    ? d.items.map(i => i.id)
    : followed
      ? resolveScenarioOrder(d.items, d.scenarioBranches, scenarioId)
      : resolveEffectiveOrder(d.items, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'));

  // Resolved for BOTH the test-bar chip labels below (orderingStateCondition) AND the interactive
  // widget itself: in plain-chip mode, OrderingCircle/SortableCustomOrderRow resolve chips on their
  // own via the ambient BlockPreviewProvider (both already render item text through PreviewHtml),
  // so this substitution is redundant-but-harmless for them and only load-bearing for the chip
  // labels; in itemsAsEquations mode there's no such ambient resolution path at all — the widget
  // only ever renders whatever `.text` it's handed, so this IS the only place the formula's
  // evaluated value gets computed before display, mirroring MCQ's own `resolvedD.options`.
  const resolvedItems = d.items.map(i => ({
    ...i,
    text: d.itemsAsEquations
      ? orderingItemDisplayText(i, true, resolvedVariables, locale, angleModeFor(d, `items.${i.id}.formula`))
      : processVariableChipHTML(i.text, rolledValues),
  }));
  // Reorders resolvedItems to match whichever order is ACTUALLY correct this roll (a matched
  // branch's own order, or the base order when branching is off) — so the 'correct' chip's shown
  // sequence and every structural-trigger summary reflect the real answer, not always the base
  // Answer Setup order once a branch has diverged from it. `null` (a coverage gap) has no
  // meaningful sequence to show at all — handled as its own "unreachable" chip below instead of
  // falling back here, since showing the base order would misleadingly imply it's still correct.
  const effectiveItems = effectiveOrder
    ? effectiveOrder.map(id => resolvedItems.find(i => i.id === id)).filter((i): i is OrderingItemDraft => !!i)
    : resolvedItems;
  const testResult = hasSubmitted ? evaluateOrdering({ touched, order: submittedOrder }, d as OrderingBlockData, resolvedVariables) : null;
  const firedIds = new Set((testResult ?? []).map(s => s.id));

  const { listed: legendStates, catchAll: participationCatchAll } =
    legendStatesFor(d.gradingMode, activeFeedbackForDisplay.states, s => s.type === 'targeted');
  const anyTargeted = legendStates.some(s => s.type === 'targeted');
  const testChips: TestBarChip[] = [...legendStates, ...(participationCatchAll ? [participationCatchAll] : [])].map(s => ({
    id: s.id,
    // The CATCH-ALL is renamed, not re-derived. Keyed on its trigger, not on `type === 'default'`
    // — No Answer is a default too, and it was taking the catch-all's name.
    condition: (s.trigger === 'any_answer' || s.trigger === 'any_response'
      ? catchAllTitle(d.gradingMode, anyTargeted, t, ORDERING_SURVEY_CATCH_ALL) : undefined)
      ?? orderingStateCondition(s, effectiveItems, t),
    // Neutral outside graded, and the catch-all loses its tick once a target exists — the same
    // rule `catchAllChips` states for the blocks whose catch-all enumerates. Ordering's cannot
    // enumerate (its answer space is every permutation), so it keeps the plain label and only
    // the tone changes.
    evaluation: d.gradingMode === 'graded' ? s.evaluation
      : anyTargeted ? 'neutral' : s.evaluation,
    active: firedIds.has(s.id),
    category: s.trigger === 'unanswered' ? 'unanswered' as const
      : s.trigger === 'any_answer' ? 'any_answer' as const
      : s.trigger === 'any_response' ? 'any_response' as const
      : 'targeted' as const,
  }));

  if (d.gradingMode === 'graded') {
    // A coverage gap (branches on, nothing resolved) means NO order can ever be marked correct
    // this roll — showing the base order's sequence here would misleadingly read as "this is still
    // the right answer" when it structurally can't fire. Same shared "unreachable" wording/styling
    // Dropdown/MCQ already use for their own structurally-dead chips.
    // `orderingReachability` enumerates the permutations and asks the evaluator's own trigger
    // matcher about each, so both verdicts below are facts rather than judgements.
    //
    // Correct dies only when there is no order to match — no items, or branching on with nothing
    // resolved. Blank items do NOT kill it: the answer key IS the order, which exists as soon as
    // the rows do, and a student can drag blank rows into it. An earlier pass here treated "setup
    // incomplete" as "unreachable" and was wrong in exactly that way; blank rows are an authoring
    // incompletion, which the issue bar already reports.
    //
    // Default Incorrect, conversely, genuinely can be covered off, and easily on a small block:
    // two items have exactly one wrong permutation, so a single `reversed` state catches every
    // wrong answer there is.
    const barReach = orderingReachability(d, effectiveOrder, resolvedItems.map(i => i.id));
    const barCanFire = (trigger: string) => barReach.states.find(x => x.trigger === trigger)?.reachable ?? false;
    const correctReachable = barCanFire('correct');
    const correctState = activeFeedbackForDisplay.states.find(s => s.trigger === 'correct');
    if (correctState) {
      testChips.push(...toTestBarChips(
        orderingChips(correctState, effectiveItems, t, d.itemsAsEquations, correctReachable, d.gradingMode, anyTargeted), {
          id: correctState.id,
          category: 'correct',
          active: firedIds.has(correctState.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    const incorrectState = activeFeedbackForDisplay.states.find(s => s.trigger === 'incorrect');
    if (incorrectState) {
      const incorrectReachable = barCanFire('incorrect');
      testChips.push(...toTestBarChips(
        orderingChips(incorrectState, effectiveItems, t, d.itemsAsEquations, incorrectReachable), {
          id: incorrectState.id,
          category: 'incorrect',
          active: firedIds.has(incorrectState.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
  }

  // A media item's `text` is its Label — never rich-text/chip-capable (see SortableItemRow's Label
  // input) — excluded so a stray chip left over in old data can't resurrect equations-mode UI.
  const hasChips = htmlHasVariableChips(d.question) || (d.itemsAsEquations
    ? d.items.some(i => getEquationVariableNames(i.formula ?? '').length > 0)
    : d.items.some(i => !i.media && htmlHasVariableChips(i.text)));
  const hasRollable = hasRollableVariables(variables);
  return { testResult, resolvedQuestion, resolvedItems, testChips, hasChips, hasRollable, matchedBranch, matchedBranchIndex, matchedEquationValue };
}

// ─── Collapsed-view display ────────────────────────────────────────────────────
// Shows the single top-priority issue (error beats warning), same as MCQ's own collapsed bar. When
// clean, falls back to the same green success bar MCQ/Dropdown/Keyword show — `isMain`-gated so it
// only renders in mobile-portrait/preview, matching every other block (the desktop card relies on
// the collapsed widget preview instead, which already displays the canonical order there).
function orderingCollapsedIssueBar(
  issues: BlockIssue[],
  gradingMode: OrderingBlockDataLocal['gradingMode'],
  isMain: boolean,
  t: (k: string, opts?: Record<string, unknown>) => string,
): React.ReactNode {
  const issueBar = collapsedIssueBar(issues);
  if (issueBar) return issueBar;
  if (isMain) return null;
  return collapsedModeBar(gradingMode, t, 'ordering');
}

// Module-level (not per-instance) — see mcq-block.tsx's identical caches for why: same
// "recomputes on every keystroke anywhere in the task, not just this block's own fields" pattern,
// fixed uniformly across every block now rather than waiting for Phase 7's own chip-collision
// check to make it expensive enough to notice here too.
const orderingIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();
const orderingTestDataCache: IssueCache<OrderingTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft -- see keyword-block. Preview's chips describe the SUBMITTED
 * answer; Behind the Scenes wants what would fire now.
 */
const orderingDraftChipsCache: IssueCache<OrderingTestData> = new WeakMap();

// ─── Main component ─────────────────────────────────────────────────────────────

export default function OrderingBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename, experienceId, taskId,
}: OrderingBlockProps) {
  const { t, i18n } = useTranslation();
  /* The top rung, asked once for the whole editor. It gates only whether a mechanism may be
     ADOPTED; each toggle below keeps its own `|| <already on>` so one in use can be switched
     off. See `useVariableMechanismAllowed` for why the insert gate does not imply this. */
  const mayVarMechanism = useVariableMechanismAllowed();
  const scope = useAnswerRefScope();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level copy is vestigial once a bank exists. */
  const initial = useRef(applyOrderingVersion(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read `push()` depends on; a ref cannot re-render, so restored HTML needs state to
   * reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedItems, setItems] = useState(initial.current.items);
  const [editedShape, setShape] = useState(initial.current.shape);
  const [editedCircularDirection, setCircularDirection] = useState(initial.current.circularDirection);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalOrderBranch[] | undefined>(initial.current.conditionalBranches);
  // "Treat items as equations" — see OrderingBlockDataLocal.itemsAsEquations' doc comment for the
  // full toggle contract. Identical role to MCQ/Dropdown's own `optionsAsEquations` state.
  const [editedItemsAsEquations, setItemsAsEquations] = useState(initial.current.itemsAsEquations ?? false);
  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read an issue list built from `block.data`, the editor panel reads one
  // built from live state, and the two are often rendered by different instances of this
  // component — so each must be gated by the settling of the value it was computed from.
  // Unchecking "Correctness depends on variables" clears the equation/branches from state — but a
  // teacher who rechecks it before closing the editor shouldn't lose what they'd typed. Holds the
  // last-known equation/branches across an off→on cycle within the same editing session; cleared
  // for real in handleDone, so it never survives an exit while the toggle is left off. Identical
  // role to MCQ/Dropdown's own `savedConditionalDraftRef`.
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle. Dropped on close, and that is
   * what makes the removal permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<OrderingBlockDataLocal['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);
  // Disambiguates trig functions in `conditionalEquation` OR any item's `formula` (once
  // itemsAsEquations is on) — one shared mode for the whole block, not per-formula, identical
  // contract to MCQ/Dropdown's own `angleMode`. See OrderingBlockData.angleMode's own doc comment.
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);
  const [armedItemId, setArmedItemId] = useState<string | null>(null);

  // Shared toolbar (matches mcq-block.tsx's pattern exactly): every item's own RichTextEditor
  // hides its own toolbar and instead reports focus here, so one anchored toolbar above the list
  // always acts on whichever item (or the question) currently has focus, rather than each item
  // carrying its own independent toolbar.
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;
  const questionEditorRef = useRef<Editor | null>(null);
  const itemEditorsRef = useRef<Map<string, Editor>>(new Map());

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, items: editedItems, shape: editedShape,
    circularDirection: editedCircularDirection, gradingMode: editedGradingMode,
    conditionalEquation: editedConditionalEquation, conditionalBranches: editedConditionalBranches,
    itemsAsEquations: editedItemsAsEquations, angleMode: editedAngleMode,
  };
  const restored = useRestoredData<OrderingBlockDataLocal>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyOrderingVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...applyOrderingVersion(initData(block.data), editorVersion), ...edited } as OrderingBlockDataLocal,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: OrderingBlockDataLocal) {
      setItems(next.items);
      setShape(next.shape);
      setCircularDirection(next.circularDirection);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setItemsAsEquations(next.itemsAsEquations ?? false);
      setAngleMode(next.angleMode ?? 'degrees');
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyOrderingVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyOrderingVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const items = isEditing ? editedItems : restored.data.items;
  /* Which item images move — asked of the ASSETS, since an item stores only an id. Read from the
     live items so adding an animated one starts asking for its description straight away. */
  const animatedAssetIds = useAnimatedAssetIds(
    useMemo(() => items.flatMap(i => (i.media?.kind === 'image' ? [i.media.assetId] : [])), [items]),
  );
  const shape = isEditing ? editedShape : restored.data.shape;

  /**
   * LEARN WHETHER EACH MEDIA item MOVES, once, and write it down.
   *
   * The motion-description check is an ERROR and reads `media.animated` so it can run without a
   * network — only the server knows whether a `.webp` animates, and a check the experience board
   * cannot run is a check the publication gate cannot read. A item picked from now on records
   * it; one authored before the field existed learns it here.
   *
   * `normalization: true` keeps it OFF THE UNDO STACK: it travels the same write path a teacher's
   * edit does, and without the flag merely opening a block leaves an undo step nobody made.
   * Written only when the stored answer actually differs, so a block that already knows emits
   * nothing at all.
   */
  useEffect(() => {
    const live = items;
    if (!live.some(it => it.media?.kind === 'image')) return;
    const healed = live.map(it => (it.media?.kind === 'image'
      && animatedAssetIds.has(it.media.assetId) !== (it.media.animated === true)
      ? { ...it, media: { ...it.media, animated: animatedAssetIds.has(it.media.assetId) } }
      : it));
    if (healed.every((it, idx) => it === live[idx])) return;
    onUpdate({ ...block.data, items: healed }, { normalization: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animatedAssetIds, block.data]);
  const circularDirection = isEditing ? editedCircularDirection : restored.data.circularDirection;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const itemsAsEquations = isEditing ? editedItemsAsEquations : (restored.data.itemsAsEquations ?? false);
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addOrderingVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addOrderingVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * Every field write, routed into the version it belongs to.
   *
   * These two already spread `currentDataRef.current` rather than naming fields, so they were
   * never at risk of the delete-by-omission that cost MCQ its bank — but they still wrote the
   * ANSWER onto the block, which on a banked block is the vestigial copy nothing reads.
   */
  const emitFields = (next: Partial<OrderingBlockDataLocal>) => {
    const base = initData(block.data);
    emit(writeOrderingVersion(base, editorVersion, { ...base, ...next } as OrderingBlockDataLocal) as unknown as Record<string, unknown>);
  };

  const storedData = initData(block.data);
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation, answers: itemsAsEquations ? items.map(o => o.formula ?? '').join('\u0000') : undefined },
    { equation: storedData.conditionalEquation, answers: storedData.itemsAsEquations ? storedData.items.map(o => o.formula ?? '').join('\u0000') : undefined },
  );

  const fields: OrderingBlockDataLocal = {
    question: questionHtml, shape, items, gradingMode, circularDirection,
    conditionalEquation, conditionalBranches, itemsAsEquations, angleMode,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  // Ordering-specific override of the shared cross-block "Incorrect" wording — used everywhere
  // THIS BLOCK renders that state's label itself (test-bar chip via orderingStateCondition, the
  // feedback panel's own locked chip below, and this hook's titleLabels/defaults, which drive
  // both the live editor and a freshly-created block's stored title). Not the shared
  // `blocks.feedbackDefaults.incorrectTitle` key — that's used by MCQ/Dropdown/etc. too, and
  // changing it would've changed their wording along with Ordering's.
  //
  // Flow is a deliberate exception, not an oversight: it overrides every block's default-incorrect
  // state with one shared, generic "Default Incorrect" string regardless of this value (see
  // flowStateLabel in flow-utils.ts) so a teacher scanning conditions across many different block
  // types in one rule sees one consistent vocabulary — decided to leave that as-is rather than
  // special-case Ordering there too.
  const incorrectAnyOtherOrderTitle = t('blocks.ordering.incorrectAnyOtherOrder');

  const {
    feedback, setFeedback, resyncFeedback, getTitle,
  } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: incorrectAnyOtherOrderTitle,
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: incorrectAnyOtherOrderTitle,
    },
    extraDefaults: [],
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      emitFields({ ...currentDataRef.current, feedback: nextFeedback });
    },
  });

  function push(overrides: Partial<OrderingBlockDataLocal> = {}) {
    // conditionalBranchesDraft comes from the ref, not currentDataRef: it is authoring state that
    // never belongs in the block's live data snapshot, but it must survive every ordinary save.
    emitFields({ ...currentDataRef.current, feedback, conditionalBranchesDraft: savedConditionalDraftRef.current, ...overrides });
  }

  resyncFeedbackRef.current = resyncFeedback;
  /**
   * FOLLOWING A SCENARIO. The engine has carried `ScenarioOrderBranch` and
   * `resolveScenarioOrder` since the family port; only the editor was missing, so an Ordering
   * block could be graded against a scenario it had no way to be linked to.
   *
   * The payload is the whole of what differs: a row carries an ORDER, which is exactly what
   * this block's conditional branch carries. Everything else is the shared hook.
   */
  const scenario = useScenarioFollower<ScenarioOrderBranch>({
    block, blocks, gradingMode, feedback, isEditing, isPreview,
    storedData: initData(block.data) as never,
    restoredRows: (restored.data as { scenarioBranches?: ScenarioOrderBranch[] }).scenarioBranches,
    initialRows: (initial.current as { scenarioBranches?: ScenarioOrderBranch[] }).scenarioBranches,
    emit: data => emit(versionStructure(block.data, data)),
    applyVersion: applyOrderingVersion,
    emitFields: next => emitFields(next as Partial<OrderingBlockDataLocal>),
    /* The order as it stands, which is this block's answer key — the same thing
       `makeConditionalBranch` seeds a numeric branch from. */
    rowTemplate: () => ({ order: items.map(i => i.id), feedback }),
    rowFromVersion: v => ({
      order: (v as unknown as { items?: { id: string }[] }).items?.map(i => i.id) ?? items.map(i => i.id),
      feedback: (v as unknown as { feedback: OrderingFeedback }).feedback,
    }),
    numeric: {
      read: () => ({ equation: conditionalEquation, branches: conditionalBranches }),
      write: (eq, br) => {
        setConditionalEquation(eq as string | undefined);
        setConditionalBranches(br as ConditionalOrderBranch[] | undefined);
      },
    },
  });

  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write below that deals with feedback CONTENT (No Answer/Correct/Incorrect/
  // Targeted) goes through these instead of `feedback`/a direct setter, so the same
  // JSX-construction code works unchanged whether branching is on or off — identical role to
  // MCQ/Dropdown's own `activeBranch`/`activeFeedbackStates` indirection. Gated on gradingMode
  // itself (not just conditionalBranches existing) for the same reason theirs is: branches are
  // authored exclusively in graded mode, and switching away must make every downstream reader fall
  // back to the plain static/top-level shape.
  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;
  /* WHICHEVER setup the panel is editing — a numeric branch and a scenario row differ only in
     what SELECTS them, and every read below this line is one path. */
  const activeSetup = scenario.activeScenario ?? activeBranch;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (scenario.activeScenario) {
      scenario.writeActiveRow({ feedback: { states: nextStates } });
    } else if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push({ conditionalBranches: nextBranches });
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push({ feedback: next });
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  /** Applies `transform` to the top-level feedback AND every branch's own feedback — needed
   * whenever an item's identity changes (text edit, reorder, delete) since a custom-order title
   * embeds the item sequence and a blank item's fallback label is position-based. Identical role
   * to MCQ/Dropdown's own `updateAllMcqFeedbackSets`/`updateAllFeedbackSets`. */
  function updateAllOrderingFeedbackSets(
    transform: (states: FeedbackState[]) => FeedbackState[],
  ): { feedback: OrderingFeedback; branches: ConditionalOrderBranch[] | undefined } {
    return {
      feedback: { states: transform(feedback.states) },
      branches: conditionalBranches?.map(b => ({ ...b, feedback: { states: transform(b.feedback.states) } })),
    };
  }

  /** Full copy, fresh ids, no content changes — used only for the very first branch, which isn't
   * a fork of anything: it's the same feedback that already existed, simply moving to live under
   * a branch instead of at the top level. Identical role to MCQ/Dropdown's own
   * `cloneFeedbackWithFreshIds`. */
  function cloneOrderingFeedbackWithFreshIds(source: OrderingFeedback): OrderingFeedback {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Structure carries over, content resets to blank if customized or carries forward unchanged
   * if it still matches that slot's default — identical role to MCQ/Dropdown's own
   * `seedBranchFeedback`, used for every branch after the first. */
  function seedOrderingBranchFeedback(source: OrderingFeedback): OrderingFeedback {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching only exists in graded mode, so the positive slot inside a branch is always
          // the 'correct' trigger, never any_answer/any_response.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  /** Seeds `order` from the CURRENT base item order — a starting point to diverge from, not a
   * value that stays in sync afterward (see ConditionalOrderBranch's own doc comment). */
  function makeConditionalOrderBranch(seedFrom: OrderingFeedback, mode: 'migrate' | 'fork'): ConditionalOrderBranch {
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      order: items.map(i => i.id),
      feedback: mode === 'migrate' ? cloneOrderingFeedbackWithFreshIds(seedFrom) : seedOrderingBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      // Restore the draft from the last time this session turned the toggle off, if any —
      // otherwise start fresh, same as before.
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalOrderBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      push({ conditionalEquation: equation, conditionalBranches: branches, conditionalBranchesDraft: undefined });
    } else {
      // Banked under its own key and out of `conditionalBranches` at once — see the identical
      // comment on the other branching blocks. Ordering's push takes an overrides object, where an
      // explicit `undefined` genuinely overwrites, so no separate helper is needed here.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      push({ conditionalEquation: undefined, conditionalBranches: undefined, conditionalBranchesDraft: draft });
    }
  }

  // A variable's presence in the QUESTION is what makes branch-based correctness meaningful —
  // identical safety net to MCQ/Dropdown's own equivalent effect: clears orphaned branches the
  // moment the last variable chip is removed, rather than leaving them silently consulted by
  // evaluateOrdering with no way for the teacher to see or edit them. Standing rule (2026-08-02,
  // applied uniformly across MCQ/Dropdown/Ordering): "Correctness depends on variables" is a fact
  // about the QUESTION's premise/scenario changing — branching's role map (here: alternate order)
  // is always a hand-authored declaration keyed on the shared equation's value, never derived from
  // evaluating an item, so a variable merely appearing in an item carries no signal that the
  // scenario has conditions under which correctness changes. Question-only, deliberately NOT
  // hasVariableInItems (that gates "Treat items as equations" instead, a few lines below) — the
  // two toggles are a one-to-one correlation with where the variable lives, not an OR.
  useEffect(() => {
    if (!isEditing) return;
    const hasVariableNow = htmlHasVariableChips(questionHtmlRef.current);
    if (!hasVariableNow && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
    // Deliberately NO equivalent auto-clear for "Treat items as equations" — unlike branching
    // (whose precondition, the question, is a different field than the branch UI it gates, so
    // losing it while your cursor is elsewhere is harmless), losing the last item variable WHILE
    // actively re-authoring a formula (e.g. clearing one item's formula to retype it) used to
    // instantly flip every item back to rich text out from under the teacher's cursor — far more
    // disruptive than a stale/unreachable toggle. Once on, this toggle now only ever turns off via
    // the explicit checkbox click below (see its own `hasVariableInItems || ` OR-with-current-state
    // render condition, which is what keeps that checkbox reachable even after every item variable
    // is gone, instead of the old auto-clear's job of preventing an unreachable stuck-on toggle).
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push({ conditionalEquation: val });
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalOrderBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push({ conditionalBranches: next });
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push({ conditionalBranches: next });
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalOrderBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push({ conditionalBranches: next });
  }

  /** Ordering-specific — driven by `OrderingCustomOrderEditor`'s `onChange` for whichever branch
   * is active, reusing that fully-generic component as-is rather than building a new drag UI. */
  function updateConditionalBranchOrder(id: string, nextOrder: string[]) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, order: nextOrder } : b);
    setConditionalBranches(next);
    push({ conditionalBranches: next });
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Live test state (collapsed canvas card, phone Test tab, Preview) ─────────
  // Identical contract to MCQ's own test-state cluster: testOrder is the widget's own live value;
  // submittedOrder is what evaluation actually runs against — the two only diverge in submit-gated
  // mode (continuous === false). `hasDragged` is Ordering's own stand-in for MCQ's "empty
  // selection" no-answer signal — the order itself is always a full permutation (there's no empty
  // state for a drag-reorder widget, see OrderingAnswer's own doc comment in ordering.ts), so
  // whether the student ever actually touched the widget is the only reliable "did they answer"
  // check; submitting without ever dragging correctly reports as unanswered.
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);

  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  const rolledValues = isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues;
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   */
  const prior = usePriorAnswer<string[]>();
  /**
   * Preview starts SCRAMBLED, and that is not optional the way MCQ's randomise toggle is.
   *
   * An ordering block whose items are already in the answer order is not a question — the student
   * presses Submit and is correct without reading them. The editor's own test widget has always
   * shuffled (`initialTestOrder`, which also rejects a roll that lands back on the canonical
   * arrangement); Preview never did, because the only thing that seeded a start order was the
   * `isSelected` effect, and Preview has no selection. So Preview showed the answer key.
   *
   * Seeded at MOUNT, not in an effect, for the same reason every restored answer is: Preview
   * remounts on navigation and on Reset, so once is enough, and an effect would fight a student
   * who has already started dragging. A restored prior answer wins — that is their own work.
   */
  const [testOrder, setTestOrder] = useState<string[]>(() => {
    if (prior) return prior;
    if (!isPreview) return [];
    const current = initData(block.data);
    return initialTestOrder(current.items, current.shape);
  });
  /**
   * THE ARRANGEMENT THIS TEST STARTED FROM, so Reset can restore it instead of inventing one.
   *
   * Ordering is the awkward case for the testing-block rule, because here the ANSWER IS THE ORDER:
   * clearing the answer necessarily means putting some arrangement back, and `handleTestReset` was
   * calling `initialTestOrder` — a fresh shuffle. That made a teacher's test unrepeatable: reset
   * and the items are somewhere else, so a different outcome could be the new answer or the new
   * layout. Remembering the start makes Reset mean "same question, try again", and Reroll is where
   * a new arrangement comes from.
   */
  const startOrderRef = useRef<string[] | null>(null);
  const [submittedOrder, setSubmittedOrder] = useState<string[]>(prior ?? []);
  // Restored as already dragged: the order on screen IS one the student arranged, so treating it
  // as untouched would re-offer the "you have not moved anything yet" state over their own work.
  const [hasDragged, setHasDragged] = useState(prior !== undefined);
  const [hasSubmitted, setHasSubmitted] = useState(prior !== undefined);
  // The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
  // of the student's typing rather than of what they chose to hand in.
  useReportAnswer(hasSubmitted ? submittedOrder : undefined);
  const [continuous, setContinuous] = useState(true);
  // Phone-only Edit/Test toggle inside the full-screen editor — identical contract to MCQ's own
  // `phoneTestMode`.
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  // Forces OrderingCircle to remount on Reset — see OrderingAnswerDisplay's own doc comment on
  // why (it only resyncs from a changed item ID SET, never from just the sequence changing).
  const [resetGeneration, setResetGeneration] = useState(0);

  useEffect(() => {
    // Also seeded for `isPreview` — the student-facing Preview render needs a real rolled value
    // and a stable starting order exactly like the isSelected test widget does.
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data);
      const start = initialTestOrder(current.items, current.shape);
      startOrderRef.current = start;
      setTestOrder(start);
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      } else if (!isPreview) {
      setTestOrder([]);
      setSubmittedOrder([]);
      setHasDragged(false);
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);
  // Phone's own entry/exit into the Test tab — mirrors the isSelected effect above exactly
  // (isSelected is always false on phone, since block selection there is tracked via
  // mobileEditingId, not selectedBlockId).
  useEffect(() => {
    if (phoneTestMode === 'test') {
      setRolledValues(rollVariableValues(variables));
      const current = initData(block.data);
      setTestOrder(initialTestOrder(current.items, current.shape));
    // NOT in Preview, for the same reason as the `isSelected` effect above: this clears the
    // test widget when the PHONE editor leaves its Test tab, and on a desktop Preview
    // `phoneTestMode` is 'edit' forever, so it ran on mount and wiped the restored answer.
    } else if (!isPreview) {
      setTestOrder([]);
      setSubmittedOrder([]);
      setHasDragged(false);
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);
  // Preview's single bottom Submit (see task-content.tsx's own doc comment on the signal) —
  // reacts to the signal CHANGING, not a per-block Submit click, since Preview has no per-block
  // Submit button any more. Reuses the exact same submittedOrder/hasSubmitted state (and therefore
  // the exact same feedback-firing machinery) the live test widget already uses — Preview is just
  // a different trigger for the same evaluation, not a separate one.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedOrder(testOrder);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  function handleTestChange(order: string[]) {
    setTestOrder(order);
    setHasDragged(true);
    if (continuous) { setSubmittedOrder(order); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedOrder(testOrder);
    setHasSubmitted(true);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedOrder(testOrder); setHasSubmitted(true); }
  }
  function handleTestReset() {
    const current = initData(block.data);
    /* The arrangement this test began with, not a new one — see `startOrderRef`. Seeded here when
       the test never had a start of its own (entering test mode is what usually sets it). */
    if (!startOrderRef.current) startOrderRef.current = initialTestOrder(current.items, current.shape);
    setTestOrder(startOrderRef.current);
    setSubmittedOrder([]);
    setHasDragged(false);
    setHasSubmitted(false);
    setResetGeneration(g => g + 1);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
    /* A new draw IS a new arrangement, and it becomes the start that Reset returns to. Ordering
       was the one block whose Reroll never re-shuffled, so once Reset stopped doing it there
       would have been no way to see a second arrangement at all. */
    {
      const current = initData(block.data);
      const next = initialTestOrder(current.items, current.shape);
      startOrderRef.current = next;
      setTestOrder(next);
    }
  }

  function handleGradingMode(mode: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push({ gradingMode: mode, feedback: { states: nextFeedbackStates } });
  }

  function handleShapeChange(next: OrderingShape) {
    setShape(next);
    push({ shape: next });
  }

  function handleCircularDirectionChange(next: OrderingCircleDirection) {
    setCircularDirection(next);
    push({ circularDirection: next });
  }

  function handleItemTextChange(id: string, text: string) {
    const next = items.map(i => (i.id === id ? { ...i, text } : i));
    // Keep any custom-order feedback title in sync with this item's new text — otherwise
    // renaming "Apple" to "Mango" would leave a stale sequence behind in its title. Every branch's
    // own feedback needs this too, not just whichever one is active in the carousel.
    const { feedback: nextFeedback, branches: nextBranches } = updateAllOrderingFeedbackSets(
      states => resyncOrderingTargetedTitles(next, states, t, itemsAsEquations)
    );
    setItems(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push({ items: next, feedback: nextFeedback, conditionalBranches: nextBranches ?? conditionalBranches });
  }

  /** Mirrors handleItemTextChange exactly, for the `formula` field instead of `text` — `text` is
   * deliberately left untouched here, see the toggle contract on OrderingBlockDataLocal.itemsAsEquations
   * for why both fields persist independently. */
  function updateItemFormula(id: string, formula: string) {
    const next = items.map(i => (i.id === id ? { ...i, formula } : i));
    const { feedback: nextFeedback, branches: nextBranches } = updateAllOrderingFeedbackSets(
      states => resyncOrderingTargetedTitles(next, states, t, itemsAsEquations)
    );
    setItems(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push({ items: next, feedback: nextFeedback, conditionalBranches: nextBranches ?? conditionalBranches });
  }

  /** Sets/clears an item's media — mutually exclusive with `formula` (an item is never both an
   * equation and a media item), so attaching media clears any pre-existing formula. `text` is
   * untouched either way — it's always the item's label regardless of what's displayed, so no
   * title resync is needed here the way handleItemTextChange/updateItemFormula need one. */
  /**
   * `seedLabel` applies in the SAME update as the media — see MCQ's updateOptionMedia. Two updaters
   * called in one tick both map over this render's `items`, so the second silently drops `media`.
   */
  function handleItemMediaChange(id: string, media: OrderingItemDraft['media'], seedLabel?: string) {
    const next = items.map(i => (i.id === id
      ? { ...i, media, formula: media ? undefined : i.formula, ...(seedLabel !== undefined ? { text: seedLabel } : {}) }
      : i));
    setItems(next);
    push({ items: next });
  }

  function handleAddItem() {
    const newItem = makeItem();
    const next = [...items, newItem];
    // A new item joins every branch's own order too (appended at the end) — branches share the
    // same item set as the base Answer Setup list, only their arrangement of it differs, so adding
    // an item here must never leave a branch missing it (see `orderBranchIssue`).
    const nextBranches = conditionalBranches?.map(b => ({ ...b, order: [...b.order, newItem.id] }));
    setItems(next);
    if (nextBranches) setConditionalBranches(nextBranches);
    push({ items: next, conditionalBranches: nextBranches ?? conditionalBranches });
  }

  function handleRemoveItem(id: string) {
    if (items.length <= MIN_ITEMS) return;
    const next = items.filter(i => i.id !== id);
    // Resync every custom-order feedback's embedded sequence, since it may have referenced the
    // removed item, and every blank item after it shifts down one fallback position ("Item 2") —
    // across every branch's own feedback too, not just the top level. Every branch's own `order`
    // also drops the removed id, for the same reason `handleAddItem` appends a new one — branches
    // share one item set with the base list, only the arrangement differs.
    const { feedback: nextFeedback, branches: resyncedBranches } = updateAllOrderingFeedbackSets(
      states => resyncOrderingTargetedTitles(next, states, t, itemsAsEquations)
    );
    const nextBranches = resyncedBranches?.map(b => ({ ...b, order: b.order.filter(itemId => itemId !== id) }));
    setItems(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push({ items: next, feedback: nextFeedback, conditionalBranches: nextBranches ?? conditionalBranches });
  }

  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  function handleItemDragStart({ active }: DragStartEvent) {
    setDraggingItemId(String(active.id));
  }
  function handleItemDragEnd({ active, over }: DragEndEvent) {
    setDraggingItemId(null);
    if (!over || active.id === over.id) return;
    if (activeBranch) {
      // Branching on: the Answer Setup list shows/reorders the ACTIVE BRANCH's own canonical
      // order, not the base item array — only the order changes per branch, item content
      // (text/media) is the same single shared set edited in place regardless of which branch is
      // selected. The base `items` array's own stored sequence is untouched here, so no title
      // resync is needed (blank-item fallback labels are numbered against `items`' own position,
      // which hasn't changed).
      const from = activeBranch.order.indexOf(String(active.id));
      const to = activeBranch.order.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      updateConditionalBranchOrder(activeBranch.id, arrayMove(activeBranch.order, from, to));
      return;
    }
    const from = items.findIndex(i => i.id === String(active.id));
    const to = items.findIndex(i => i.id === String(over.id));
    if (from === -1 || to === -1) return;
    const next = arrayMove(items, from, to);
    // Reordering items shifts any blank item's fallback position ("Item 2") embedded in a
    // custom-order title — across every branch's own feedback too. Deliberately does NOT touch any
    // branch's own `order` (an id-keyed permutation, unaffected by the Answer Setup list's own
    // display order changing).
    const { feedback: nextFeedback, branches: nextBranches } = updateAllOrderingFeedbackSets(
      states => resyncOrderingTargetedTitles(next, states, t, itemsAsEquations)
    );
    setItems(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push({ items: next, feedback: nextFeedback, conditionalBranches: nextBranches ?? conditionalBranches });
  }
  function handleItemDragCancel() {
    setDraggingItemId(null);
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const usedTriggers = new Set(targeteds.map(s => s.trigger));
    // First not-yet-used structural trigger available for this shape, falling back to a fresh
    // custom-order trigger once they're all taken — matches every other block's "next unused
    // option" convention. Seeded from whichever order is currently canonical for feedback-authoring
    // purposes (the active branch's own order, or the base item order when branching is off) — the
    // same "starting point to diverge from" reasoning `makeConditionalOrderBranch` itself uses.
    const nextStructural = availableStructuralTriggers(shape).find(k => !usedTriggers.has(k));
    const seedOrder = activeBranch ? activeBranch.order : items.map(i => i.id);
    const trigger = nextStructural ?? buildCustomOrderTrigger(seedOrder);
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: orderingTargetedTitle(trigger, items, t, itemsAsEquations),
      trigger, content: '', evaluation: gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  const [draggingFeedbackId, setDraggingFeedbackId] = useState<string | null>(null);
  function handleFeedbackTargetedDragStart({ active }: DragStartEvent) {
    setDraggingFeedbackId(String(active.id));
  }
  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    setDraggingFeedbackId(null);
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }
  function handleFeedbackTargetedDragCancel() {
    setDraggingFeedbackId(null);
  }

  function handleDone() {
    push();
    // Closing is what makes a switched-off branch set unrecoverable: the retained draft is
    // dropped from the saved block and undo becomes the only way back.
    if (savedConditionalDraftRef.current !== undefined && conditionalBranches === undefined) push({ conditionalEquation: undefined, conditionalBranches: undefined, conditionalBranchesDraft: undefined });
    savedConditionalDraftRef.current = undefined;
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // Both bounds judged on the RAW item count, which is also what makes them consistent with each
  // other — too-many always was.
  //
  // Too-few used to require FILLED items, on the reasoning that a blank item is not yet a real
  // thing to order. True of the finished question, but it made choosing the SHAPE conditional on
  // having typed the content: three empty rows on screen and circular still greyed out, with
  // nothing saying that text was the missing ingredient. Enablement is structural, validity is the
  // issue layer's job — and the issue layer already does its half, raising
  // `setup-incomplete` off `filled.length < MIN_CIRCULAR_ITEMS` (see orderingIssues), so
  // nothing is lost by letting the control turn on first.
  const circularTooFewItems = items.length < MIN_CIRCULAR_ITEMS;
  const circularTooManyItems = items.length > MAX_CIRCULAR_ITEMS;
  const circularDisabled = circularTooFewItems || circularTooManyItems;
  const atCircularItemCap = shape === 'circular' && items.length >= MAX_CIRCULAR_ITEMS;
  const circularDescribedBy = circularTooFewItems ? 'ordering-circular-min-items-warning' : 'ordering-circular-max-items-warning';

  // Text-only, radio-list style — matches the settingsSlot convention actually established by the
  // blocks this project has audited so far (MCQ, Dropdown, Keyword all use CheckboxRow's own
  // greyed-out-not-hidden disabled treatment for an unavailable option; none of them use an icon
  // as the primary distinguishing device for a choice). Shape is genuinely single-select — exactly
  // one of these three is ever true at once — so RadioGroup (radio semantics, same visual language
  // as CheckboxRow) is the correct fit, not a segmented pill toggle.
  const SHAPES: { id: OrderingShape; label: string }[] = [
    { id: 'vertical', label: t('blocks.ordering.shapeVertical') },
    { id: 'horizontal', label: t('blocks.ordering.shapeHorizontal') },
    { id: 'circular', label: t('blocks.ordering.shapeCircular') },
  ];

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now, not a disabled snapshot — Preview behaves exactly like the live
    // test widget (drag to reorder, see real feedback once submitted), triggered by the
    // page-level bottom Submit (see the previewSubmitSignal effect above) instead of an in-block
    // one. rolledValues/testOrder are seeded by the same effect the isSelected branch below uses,
    // gated on `isPreview` too (see its own comment).
    const d = buildNormalizedOrderingData(initData(block.data), t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const { resolvedQuestion, resolvedItems, testResult } = memoizedByKey(
      /* The DRAWN VERSION is part of the key, or this cache answers for whichever version it saw
         first and the content never moves. */
      orderingTestDataCache, block.data,
      [variables, t, rolledValues, submittedOrder, hasDragged, hasSubmitted, i18n.language,
       taskPreviewRoll?.[versionRollKey(block.id)]],
      () => computeOrderingTestData(d, variables, t, rolledValues, submittedOrder, hasDragged, hasSubmitted, i18n.language, scenario.activeScenario?.scenarioId),
    );
    /* The DRAFT: the order as currently arranged, treated as though it had been handed in.
       `hasDragged` is forced too, or an untouched list reads as no answer when it is one. */
    const { testChips: draftChips, testResult: draftStates } = memoizedByKey(
      orderingDraftChipsCache, block.data,
      /* The DRAWN VERSION belongs in the key, like every other memo on a banked block: without
         it this cache answers for whichever version it saw first and the chips never move when
         the student draws a different one. `invariants.test.ts` catches the omission. */
      [variables, t, rolledValues, testOrder, i18n.language, scenario.activeScenario?.scenarioId,
       taskPreviewRoll?.[versionRollKey(block.id)]],
      () => computeOrderingTestData(d, variables, t, rolledValues, testOrder, true, true,
        i18n.language, scenario.activeScenario?.scenarioId),
    );
    return (
      <OrderingAnswerDisplay
          scenario={scenario.ghostedScenario}
        question={resolvedQuestion}
        items={resolvedItems}
        shape={d.shape}
        order={testOrder}
        onChange={(order) => { setTestOrder(order); setHasDragged(true); }}
        sensors={sensors}
        rolledValues={rolledValues}
        resetGeneration={resetGeneration}
        circularDirection={d.circularDirection}
      >
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftChips} states={draftStates} />
        {renderOrderingFiredFeedback(testResult, rolledValues)}
      </OrderingAnswerDisplay>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedOrderingData(initData(block.data), t, shownVersionId);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    // Every currently-active issue for this block, computed once — read by this bar (top-priority
    // one only) and the live test bar's own notices panel — identical role to MCQ's own
    // mcqIssues consumption.
    /* What a recall chip in these options could show. Rebuilt per render and therefore keyed
       into the cache below by CONTENT — its identity would recompute every block's issues on
       every render, and leaving it out would hold a stale warning after the source's options
       were edited in the other task. */
    const recalls = recallOptionSpaces(d.items.map(i => i.text), scope, variables);
    const issues = settleStored(memoizedByKey(orderingIssuesCache, block.data, [recallSpacesKey(recalls), t, variables, shownVersionId, animatedAssetIds], () => orderingIssues(d, { ...issueCtx, recalls })));
    const { resolvedQuestion, resolvedItems, testResult, testChips, hasChips, hasRollable, matchedBranch, matchedBranchIndex, matchedEquationValue } = memoizedByKey(
      // Keyed on the shown version too — see the preview branch's note.
      orderingTestDataCache, block.data,
      [variables, t, rolledValues, submittedOrder, hasDragged, hasSubmitted, i18n.language, shownVersionId],
      () => computeOrderingTestData(d, variables, t, rolledValues, submittedOrder, hasDragged, hasSubmitted, i18n.language, scenario.activeScenario?.scenarioId),
    );
    const orderingAriaLabel = d.question
      ? t('blocks.ordering.editAriaBaseWithQuestion', { question: stripHtmlForLabel(d.question).slice(0, 80) })
      : t('blocks.ordering.editAriaBase');

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<ListOrdered className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.ordering.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={orderingAriaLabel}
        answerBarSlot={() => orderingCollapsedIssueBar(issues, d.gradingMode, isMain, t)}
        testEvaluation={testResult ? (testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testChips,
          onReset: handleTestReset,
          // Versions are rollable too: a banked block can be rerolled with no chips in it.
          /* OFFERED WHENEVER ANYTHING IS RANDOM. Ordering ALWAYS shuffles its start, so once Reset
       stopped re-shuffling, a block with no variable and no bank needed this to see a second
       arrangement. Two items is the floor: one has no other order to take. */
    onReroll: ((hasChips && hasRollable) || items.length > 1)
      ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump straight to editing it —
          // identical role to MCQ/Dropdown's own topNotices; teacher-only diagnostic, so it belongs
          // in this panel, not `children` (the frozen, student-facing content area).
          /* One row for everything this test resolved to — shared with the phone bar and every
             other banked block. See `version-branch-notice.tsx`. */
          topNotices: (
            <VersionBranchNotice
              versions={scenario.testPager?.versions ?? shownVersions}
              activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
              onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
              noun={scenario.testPager?.noun ?? 'version'}
              editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={matchedBranch && matchedBranchIndex !== -1 ? {
                index: matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: matchedEquationValue,
                operator: matchedBranch.operator,
                threshold: matchedBranch.threshold,
                thresholdHigh: matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (matchedBranch && matchedBranchIndex !== -1) requestBranchJump(block.id, matchedBranchIndex);
                editShownVersion(matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected ? (
          <OrderingAnswerDisplay
          scenario={scenario.ghostedScenario}
            question={resolvedQuestion}
            items={resolvedItems}
            shape={d.shape}
            order={testOrder}
            onChange={handleTestChange}
            sensors={sensors}
            rolledValues={rolledValues}
            resetGeneration={resetGeneration}
            circularDirection={d.circularDirection}
          >
            {renderOrderingFiredFeedback(testResult, rolledValues)}
          </OrderingAnswerDisplay>
        ) : (
          <>
            <BlockQuestion html={d.question} spaced showEmptyNote />
            {/* Inoperable widget preview, canonical order (not shuffled — unlike the live test
                widget above, this is showing the teacher "how will this look", not simulating a
                student's shuffled start), arranged per the configured shape. Only when there's
                actually something valid to show — the warning bar above already covers the
                incomplete-setup case. */}
            {!isSetupIncomplete(d) && <OrderingWidgetPreview items={d.items} shape={d.shape} circularDirection={d.circularDirection} itemsAsEquations={d.itemsAsEquations} />}
          </>
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  // Live in-editor issues — the exact same computation the collapsed bar uses, run against the
  // editor's own live state instead of `initData(block.data)`, so the sticky warningBarSlot below
  // updates the instant something changes without needing to close/reopen the editor. Identical
  // role to MCQ's own `editorIssues`/`liveD`. missing-feedback-text is excluded from the bar
  // because each blank feedback card already shows its own warning directly (FeedbackCard's own
  // MessageSquareOff indicator).
  const liveD: OrderingBlockDataLocal = {
    question: questionHtmlRef.current, shape, items, gradingMode, feedback, circularDirection,
    conditionalEquation, conditionalBranches, itemsAsEquations, angleMode,
    /* The card grades off this, so it must carry the link and the rows — otherwise the card and
       the panel disagree about whether this block follows anything. */
    followsBlockId: initData(block.data).followsBlockId, scenarioBranches: scenario.rows,
  };
  const editorIssues = settleLive(orderingIssues(liveD, issueCtx));
  // ONE derivation, read from the issues themselves. This set used to re-run the duplicate and
  // collision checks a second time purely to decide which rows to ring, which is the pattern
  // `BlockIssue.fields` exists to end: the check that found the fault now says which rows it was
  // in, so the ring and the message cannot describe different rows.
  const collidingItemIds = issueFieldIds(editorIssues);
  // Highlights the specific offending field, not just the text description in the warning bar —
  // matches MCQ/Dropdown's own hasEditorError convention exactly, so a teacher can find the
  // problem at a glance instead of hunting for it after reading the bar.
  const hasEditorError = (...keys: string[]) => editorIssues.some(i => i.severity === 'error' && keys.includes(i.key));

  // ── Phone "Test" tab ─────────────────────────────────────────────────────────
  // Only ever rendered while `isPhone` (InputBlockFrame ignores it otherwise), and only replaces
  // the editingSlot content. Identical role to MCQ's own phone Test tab: same
  // computeOrderingTestData the desktop collapsed card uses, fed `liveD` instead of
  // `initData(block.data)`.
  const phoneTest = isPhone
    ? computeOrderingTestData(liveD, variables, t, rolledValues, submittedOrder, hasDragged, hasSubmitted, i18n.language, scenario.activeScenario?.scenarioId)
    : null;
  // Items in whichever order is currently canonical for feedback-authoring purposes — the active
  // branch's own order, or the base item order when branching is off — used wherever this file
  // checks/shows what a targeted feedback trigger overlaps with or matches. Degrades gracefully
  // (a shorter list) when the active branch's own `order` is itself desynced from the current item
  // set — already surfaced separately via `orderBranchIssue`.
  const activeCanonicalItems = activeBranch
    ? activeBranch.order.map(id => items.find(i => i.id === id)).filter((i): i is OrderingItemDraft => !!i)
    : items;
  const phoneTestSlot = phoneTest ? (
    <OrderingAnswerDisplay
          scenario={scenario.ghostedScenario}
      question={phoneTest.resolvedQuestion}
      items={phoneTest.resolvedItems}
      shape={liveD.shape}
      order={testOrder}
      onChange={handleTestChange}
      sensors={sensors}
      rolledValues={rolledValues}
      resetGeneration={resetGeneration}
      circularDirection={liveD.circularDirection}
    >
      {renderOrderingFiredFeedback(phoneTest.testResult, rolledValues)}
    </OrderingAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: ((phoneTest.hasChips && phoneTest.hasRollable) || items.length > 1) ? handleTestReroll : undefined,
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? shownVersions}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here: on a phone, Edit and Test are two tabs over ONE session. */
        onSelectVersion={scenario.testPager?.onSelect ?? selectVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  /**
   * No mode gate. Ordering's triggers are STRUCTURAL — reversed, an adjacent swap, a custom order
   * — and none of them presupposes a right answer: "they reversed it" is a description of an
   * arrangement, not a verdict on one. The gate was the last survivor of "outside graded, nothing
   * but the catch-all can fire", and it meant feedback authored in graded carried over into
   * survey while the panel said there was nothing to author.
   */
  const canAddMore = targetedStates.length < 5;

  // Non-blocking heads-up when a teacher-authored custom order coincides with the correct answer
  // (it can never fire — the terminal correct-check always wins first) or overlaps another
  // *actively configured* feedback (both would fire together on that exact submission) — a custom
  // order that merely happens to match, say, off-by-one is irrelevant noise if no off-by-one
  // feedback exists. Symmetric: both sides of a colliding pair get the warning, not just the
  // custom one — matches GPS's feedbackOverlapWarning/feedbackOverlapsCorrectWarning convention.
  // Checked against `activeCanonicalItems` (the active branch's own order when one is selected),
  // not always the base item order — what's correct under THIS branch is what a branch's own
  // targeted feedback needs validating against.
  const allTargetedTriggers = targetedStates.map(s => s.trigger);
  const overlappingTriggerPairs = findOrderingOverlappingFeedbackPairs(allTargetedTriggers, activeCanonicalItems, shape);
  const overlappingTriggerSet = new Set(overlappingTriggerPairs.flat());
  function orderingFeedbackWarning(state: FeedbackState): string | undefined {
    const customOrder = parseCustomOrderTrigger(state.trigger);
    // Graded only. "This matches the correct order" presupposes one, and outside graded the
    // canonical sequence is just another arrangement a teacher may legitimately want to speak to
    // — the issue list has always been gated this way; the card's own warning was not.
    if (gradingMode === 'graded' && customOrder && doesOrderingCustomOrderMatchCorrect(customOrder, activeCanonicalItems, shape)) {
      // See orderingIssues' own comment on why circular gets its own wording — a rotation of
      // the canonical order legitimately "matches" there even though it doesn't look identical.
      return t(`blocks.ordering.${shape === 'circular' ? 'feedbackOverlapsCorrectWarningCircular' : 'feedbackOverlapsCorrectWarning'}`);
    }
    if (overlappingTriggerSet.has(state.trigger)) return t('blocks.ordering.feedbackOverlapWarning');
    return undefined;
  }

  const draggingFeedbackState = draggingFeedbackId ? targetedStates.find(s => s.id === draggingFeedbackId) : undefined;

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleFeedbackTargetedDragStart}
      onDragEnd={handleFeedbackTargetedDragEnd}
      onDragCancel={handleFeedbackTargetedDragCancel}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                title={s.title} content={s.content} evaluation={displayEvaluation(gradingMode, s.evaluation)}
 /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
    had collapsed sprang back open every time they flipped modes to compare. */
 collapseSignal={gradingMode}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={orderingFeedbackWarning(s)}
                titleLocked
                /* Nothing is correct outside graded, so there is no incorrect to cycle TO — the
                   same lock every other block's targeted card already carried. */
                evalLocked={gradingMode !== 'graded'}
                triggerZone={<OrderingTriggerEditor trigger={s.trigger} onChange={trigger => updateActiveFeedbackState(s.id, { trigger, title: orderingTargetedTitle(trigger, items, t, itemsAsEquations) })} items={items} shape={shape} sensors={sensors} t={t} evaluation={displayEvaluation(gradingMode, s.evaluation)} usedKinds={targetedStates.filter(other => other.id !== s.id).map(other => orderingTriggerKind(other.trigger))} itemsAsEquations={itemsAsEquations} />}
                triggerZoneSummary={<OrderingTriggerChip trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} items={items} t={t} itemsAsEquations={itemsAsEquations} />}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {draggingFeedbackState ? (
          <div className='flex items-center gap-1.5 rounded-lg border border-ws-primary bg-ws-surface px-3 py-2 shadow-lg' style={{ cursor: 'grabbing' }}>
            <GripVertical className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' />
            <span className='min-w-0 flex-1 truncate text-sm font-medium text-ws-text'>{getTitle(draggingFeedbackState)}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );

  // A branch whose own `order` is desynced from the current item set (orderBranchIssue) can never
  // grade correctly if it resolves — the direct Ordering analog of MCQ's "branch's correct option
  // is blank" inactive-marking, just at the branch-order level instead of a per-option one. Passed
  // as "no canonical order" so the derivation reaches the same verdict the panel used to assert.
  const activeBranchOrderInvalid = !!activeBranch && orderBranchIssue(activeBranch, items) !== null;
  // The same enumeration the test bar reads. Note what is NOT here any more: `setupIncomplete`.
  // Blank item rows are an authoring incompletion, not an unreachable Correct — the answer key is
  // the order itself, so a student can still drag the rows into it and be graded right.
  const panelReach = orderingReachability(
    { gradingMode, feedback: { states: activeFeedbackStates }, shape },
    activeBranchOrderInvalid ? null : activeCanonicalItems.map(i => i.id),
    items.map(i => i.id),
  );
  const panelCanFire = (trigger: string) => panelReach.states.find(x => x.trigger === trigger)?.reachable ?? false;
  const correctSetupIncomplete = gradingMode === 'graded' && !panelCanFire('correct');
  // Every wrong order already has its own targeted feedback — on two items that takes exactly one
  // `reversed` state, since there is only one wrong order to catch.
  const incorrectUnreachable = gradingMode === 'graded' && !panelCanFire('incorrect');

  // ── Variable-driven branching (Answer Setup section) ──────────────────────
  // Question-only (see the auto-clear effect's own doc comment for why this is deliberately NOT
  // ORed with hasVariableInItems below) — "Correctness depends on variables" and "Treat items as
  // equations" are a one-to-one correlation with where the variable lives, not a shared gate.
  const hasVariable = htmlHasVariableChips(questionHtmlRef.current);
  const showConditionalUI = hasVariable && gradingMode === 'graded';
  // Specifically about ITEMS, not the question — independent of showConditionalUI (the equations
  // toggle is meaningful in survey/optional mode too, unlike branching).
  const hasVariableInItems = mayVarMechanism
    && items.some(i => !i.media && (htmlHasVariableChips(i.text) || getEquationVariableNames(i.formula ?? '').length > 0));
  const isConditionalEnabled = conditionalBranches !== undefined;
  const conditionalBranchesList = conditionalBranches ?? [];
  const clampedBranchIndex = Math.min(activeBranchIndex, Math.max(0, conditionalBranchesList.length - 1));
  const hasEquation = !!conditionalEquation?.trim();
  const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;
  const equationWorks = hasEquation && !equationInvalid;

  const itemFormulaIssueIds = new Set(
    itemsAsEquations
      ? checkOptionFormulaIssues(
          items,
          variables,
          angleMode
        ).issues.flatMap(i => i.optionIds)
      : []
  );

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => {
      // Shows the actual canonical sequence (graded mode) instead of a bare "Correct" label —
      // same specificity fix as OrderingTriggerChip's custom-order chip, reusing the identical
      // orderingStateCondition the live-test chip legend already uses so the two surfaces can
      // never disagree. any_answer/any_response (survey/optional) have no fixed sequence to
      // show, so orderingStateCondition correctly falls back to its generic title text there.
      // Reads `activeCanonicalItems` (the active branch's own order when one is selected), not
      // always the base item order — this chip needs to show what's ACTUALLY correct right now.
      // Colored/iconed the same way as every other block's own Correct chip (green + check) —
      // this state's evaluation is always 'correct' by construction (it's the positive default
      // slot), so unlike the targeted-trigger chips there's no ambiguity to lock to a cycler for.
      // Not `shrink-0` — same overflow-into-the-chevron bug as OrderingTriggerChip's identical
      // custom-order chip (see its own comment): this one can run even longer, since Correct
      // always shows the FULL item sequence, not just whatever fits a custom order's own summary.
      const inactive = correctSetupIncomplete;
      // The shared row, not a hand-built chip: it carries the same classes this used to spell out
      // (which is where the `max-w-48 truncate` bug came from — a local cap that let the full item
      // sequence ellipsize on one line while every other block's chip wrapped to two).
      const correctChipNode = (
        <ConditionChipRow
          chips={orderingChips(state, activeCanonicalItems, t, itemsAsEquations, !inactive, gradingMode, targetedStates.length > 0, ORDERING_SURVEY_CATCH_ALL)}
          unreachableLabel={t('blocks.feedbackPanel.unreachable')}
        />
      );
      return (
        <FeedbackCard fieldId={state.id}
          key={state.id}
          locked inactive={inactive}
          triggerZone={correctChipNode}
          triggerZoneSummary={correctChipNode}
          title={catchAllTitle(gradingMode, targetedStates.length > 0, t, ORDERING_SURVEY_CATCH_ALL) ?? getTitle(state)}
          content={state.content}
          /**
           * The tick goes once a target exists, exactly as in every other block. Ordering's
           * catch-all cannot ENUMERATE what it still catches — its answer space is every
           * permutation — so the label stays and only the tone changes.
           */
          evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
          onTitleChange={() => {}}
          onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
          onEvaluationCycle={() => {}}
          onDelete={() => {}}
          activeEditorRef={activeEditorRef}
          setActiveEditor={setActiveEditor}
        />
      );
    });

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => {
      // Was `lockedTriggerLabel`, which is a plain label rather than a chip — so this card reached
      // "Any Other Order" by a different route than the legend did, and the two agreed only because
      // both happened to read the same key. Now it draws the derivation, like every other card.
      const incorrectChipNode = (
        <ConditionChipRow
          chips={orderingChips(state, activeCanonicalItems, t, itemsAsEquations, !incorrectUnreachable)}
          unreachableLabel={t('blocks.feedbackPanel.unreachable')}
        />
      );
      return (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={incorrectUnreachable}
        triggerZone={incorrectChipNode}
        triggerZoneSummary={incorrectChipNode}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
      );
    });

  // ── Feedback branch scroller ──────────────────────────────────────────────
  // Same shared activeBranchIndex the Answer Setup carousel below uses, in a separate control here
  // so switching branches can never accidentally leave the two views out of sync — identical role
  // to MCQ/Dropdown's own `feedbackBranchScroller`.

  // Graded-only, like every other branching surface (activeBranch, showConditionalUI): branches are
  // read straight from persisted data, so a block authored with branches and later switched to
  // Participation/Optional kept showing this scroller above states those branches can never reach.
  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // The item list itself — always the SAME shared items (text, media) no matter which condition
  // is selected in the carousel above it; only their ARRANGEMENT changes (that branch's own
  // `order`, or the base order otherwise). Computed once, rendered from two different spots below
  // (nested inside the condition envelope when a branch is active, standalone otherwise) — same
  // `optionListJsx` technique MCQ's own conditional-branch UI uses, so both spots can never drift
  // out of sync with each other.
  // With the list in both states, for the reason set out in MCQ: adding an item is an answer-set
  // operation, and when branching is on the answer set is inside the carousel.
  const addItemButton = (
          <AddRowButton
            label={t('blocks.ordering.addItem')}
            onClick={handleAddItem}
            disabled={atCircularItemCap}
            describedBy={atCircularItemCap ? 'ordering-circular-item-cap-reached' : undefined}
          />
  );

  const itemListJsx = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleItemDragStart}
      onDragEnd={handleItemDragEnd}
      onDragCancel={handleItemDragCancel}
      accessibility={dndA11y(t, activeCanonicalItems.map(x => x.id), 'item')}
    >
      <SortableContext items={activeCanonicalItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-2'>
          {activeCanonicalItems.map(item => (
            <SortableItemRow
              key={item.id}
              item={item}
              itemCount={items.length}
              armedItemId={armedItemId}
              setArmedItemId={setArmedItemId}
              onTextChange={handleItemTextChange}
              onRemove={handleRemoveItem}
              itemEditorsRef={itemEditorsRef}
              activeEditorRef={activeEditorRef}
              setActiveEditor={setActiveEditor}
              formulaMode={itemsAsEquations}
              onFormulaChange={updateItemFormula}
              formulaHasError={itemFormulaIssueIds.has(item.id)}
              hasDuplicate={collidingItemIds.has(item.id)}
              variables={variables}
              rolledValues={rolledValues}
              angleMode={angleMode}
              experienceId={experienceId}
              taskId={taskId ?? ''}
              taskBlocks={blocks ?? []}
              onMediaChange={handleItemMediaChange}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {draggingItemId ? (() => {
          const draggingItem = items.find(i => i.id === draggingItemId);
          return draggingItem ? <OrderingItemDragPreview item={draggingItem} itemsAsEquations={itemsAsEquations} /> : null;
        })() : null}
      </DragOverlay>
    </DndContext>
  );

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      icon={<ListOrdered className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.ordering.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.ordering.closeEditor')}
      onClose={handleDone}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>
            <RadioGroup fieldId='shape'
              aria-label={t('blocks.ordering.shapeLabel')}
              value={shape}
              onChange={handleShapeChange}
              options={SHAPES.map(({ id, label }) => ({
                value: id,
                label,
                disabled: id === 'circular' && circularDisabled,
                describedBy: id === 'circular' && circularDisabled ? circularDescribedBy : undefined,
              }))}
            />
            {circularTooFewItems && (
              <p id='ordering-circular-min-items-warning' className='text-xs italic text-ws-muted'>{t('blocks.ordering.shapeCircularMinItemsWarning')}</p>
            )}
            {circularTooManyItems && (
              <p id='ordering-circular-max-items-warning' className='text-xs italic text-ws-muted'>{t('blocks.ordering.shapeCircularMaxItemsWarning')}</p>
            )}
            {/* Nested under Circular (Circular's own option, not a sibling section) — always
                rendered, never conditionally mounted, so it's a stable, persistent part of the
                panel rather than something that pops in/out as Shape changes. No header of its own
                (the indent alone reads as "belongs to Circular"); greyed out via each option's own
                `disabled` (the same treatment CheckboxRow/RadioGroup already use for an unavailable
                choice) whenever anything other than Circular is selected, matching how Circular's
                own option greys out rather than disappears when out of range. */}
            <div className='ml-7 -mt-2'>
              {/* Segmented, not radio: the two options are the same circle drawn one way or the
                  other, and nothing else in the drawer changes with the choice. Shape above stays a
                  RadioGroup because picking Circular is what reveals this control at all. */}
              <SegmentedControl fieldId='circularDirection'
                aria-label={t('blocks.ordering.circularDirectionLabel')}
                value={circularDirection}
                onChange={v => handleCircularDirectionChange(v as OrderingCircleDirection)}
                options={[
                  { id: 'clockwise', label: t('blocks.ordering.circularDirectionClockwise') },
                  { id: 'counterclockwise', label: t('blocks.ordering.circularDirectionCounterclockwise') },
                ]}
                disabled={shape !== 'circular'}
                stretch
              />
            </div>
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.ordering.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: scenario.feedbackScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            {/* The three-way offer, when linking meets an existing bank — §7.1. */}
            {scenario.remapModal}
            {/* The link control and what it points at, for BOTH kinds of link — see
                `VariesWithSection`, which decides on its own whether a DRIVEN block
                needs it. This was five lines of identical JSX in twelve blocks. */}
            <VariesWithSection
              following={!!scenario.activeScenario}
              linkMenu={scenario.linkMenu}
              stimulus={scenario.editorScenario}
            />
            {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
                the answer setup, because a driven axis varies the question too. Renders its
                children bare when this block is not driven. See `DrivenRowFrame`. */}
            <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
            {/* A bank is a niche feature: in the heading it is available without being advertised. */}
            <EditorSectionHeading
              label={t('blocks.areas.question')}
              divider={!!scenario.activeScenario}
              action={(
                <div className='flex items-center gap-2'>
                {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2):
                    versions are many stems each with one answer setup, a scenario is one stem
                    with many, and the two cannot both be the answer key. */}
                {!scenario.following && (
                <VersionSelector
                  versions={(initData(block.data).versions ?? []) as { id: string }[]}
                  activeId={editorVersionId}
                  onSelect={selectVersion}
                  onAdd={() => {
                    const next = addOrderingVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                    emit(versionStructure(block.data, next));
                    const added = next.versions?.[next.versions.length - 1];
                    if (added) selectVersion(added.id, next);
                  }}
                  onRemove={(id) => {
                    const next = removeOrderingVersion(initData(block.data), id);
                    emit(versionStructure(block.data, next));
                    const landing = next.versions?.[0];
                    selectVersion(landing?.id, next);
                    setTestVersion(landing?.id);
                  }}
                />
                )}
                {/* Silent once there is a Varies-With section, which is where the
                    control moves to — see `QuestionHeadingLink`. Written as
                    `!activeScenario` it knew about scenarios only, so a DRIVEN block
                    showed the link twice and the heading overflowed sideways. */}
                <QuestionHeadingLink
                  following={!!scenario.activeScenario}
                  linkMenu={scenario.linkMenu}
                />
                </div>
              )}
            />
            <BlockQuestionEditor
              value={questionHtml}
              onChange={html => {
                // The render-time SNAPSHOT has to move with the ref, not just the ref.
                // `currentDataRef.current` is rebuilt during render, and this handler runs before any
                // re-render, so every push path would otherwise spread the PREVIOUS question and leave
                // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
                // this by reading questionHtmlRef live at push time; these blocks bake the question into
                // `fields` instead, so the two have to be kept in lockstep here.
                questionHtmlRef.current = html;
                setQuestionHtml(html);
                currentDataRef.current = { ...currentDataRef.current, question: html };
                push();
              }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {(showConditionalUI || hasVariableInItems || itemsAsEquations) && (
              <div className='flex flex-col gap-2'>
                {showConditionalUI && (
                  <BranchingCorrectsRow
                    checked={isConditionalEnabled}
                    onToggle={() => toggleConditionalCorrectness(!isConditionalEnabled)}
                  />
                )}

                {(hasVariableInItems || itemsAsEquations) && (
                  <CheckboxRow fieldId='itemsAsEquations'
                    checked={itemsAsEquations}
                    onToggle={() => {
                      const next = !itemsAsEquations;
                      // Symmetric content-flow, applied uniformly to every item (2026-08-02
                      // standing rule, mirrors MCQ/Dropdown's own identical toggle — see MCQ's own
                      // doc comment for the full reasoning; MCQ has media too, so this isn't
                      // Ordering-specific):
                      // ON: media is always wiped (equations mode suppresses it, never coexists
                      // per-item) and an item's plain text transfers into `formula` when that text
                      // is ALREADY a valid, parseable formula (e.g. "{n}+10" with no surrounding
                      // prose) — verified by actually evaluating it against sample values, not
                      // guessed from syntax, so a genuine sentence or an image/audio item both
                      // correctly end up with a BLANK formula, never overwriting an item that
                      // already has its own authored formula.
                      // OFF: every item's CURRENT formula (blank or not) is copied back into
                      // `text` as real rich content (a genuine re-editable chip for any `{name}`
                      // it contains, not literal braces) — so a round trip with nothing
                      // re-authored in between ends up BLANK (not restored to whatever the item
                      // originally was before turning equations mode on: media/prose that couldn't
                      // become a formula is a real, accepted loss), while a formula the teacher DID
                      // author while equations mode was on survives the switch back as literal
                      // editable text.
                      const nextItems = next
                        ? items.map(i => {
                            if (i.formula?.trim()) return { ...i, media: undefined };
                            const stripped = stripHtmlForLabel(i.text);
                            const transferable = stripped.trim() !== '' && evaluateEquation(stripped, minSampleValues(variables), angleMode) !== null;
                            return { ...i, media: undefined, formula: transferable ? stripped : '' };
                          })
                        : items.map(i => ({ ...i, text: formulaToRichText(i.formula ?? '', new Set(variables.map(v => v.name))) }));
                      setItemsAsEquations(next);
                      setItems(nextItems);
                      // Every targeted state's title is derived from whichever source (text or
                      // formula) is active — flipping the toggle without resyncing left titles
                      // baked under the OLD mode until some unrelated edit happened to trigger a
                      // resync, so Flow (which reads the stored title directly, never
                      // live-recomputed) could show a title mixing both modes' content depending
                      // on authoring order. Same resync every other item-mutating handler already
                      // runs (handleItemTextChange/updateItemFormula/handleRemoveItem/
                      // handleItemDragEnd).
                      const { feedback: nextFeedback, branches: nextBranches } = updateAllOrderingFeedbackSets(
                        states => resyncOrderingTargetedTitles(nextItems, states, t, next)
                      );
                      setFeedback(nextFeedback);
                      if (nextBranches) setConditionalBranches(nextBranches);
                      push({ items: nextItems, feedback: nextFeedback, conditionalBranches: nextBranches ?? conditionalBranches, itemsAsEquations: next });
                    }}
                    label={t('blocks.ordering.itemsAsEquationsToggle')}
                  />
                )}

                {isConditionalEnabled && activeBranch && (
                  <BranchConditionCarousel
                    seedVariable={(() => { const n = variableChipNames(questionHtmlRef.current); return n.length === 1 ? n[0] : undefined; })()}
                    ref={branchPrevButtonRef}
                    equation={conditionalEquation ?? ''}
                    onEquationChange={updateConditionalEquation}
                    variables={variables}
                    rolledValues={rolledValues}
                    angleMode={angleMode}
                    equationInvalid={equationInvalid}
                    equationWorks={equationWorks}
                    hasError={hasEditorError('no-equation', 'invalid-equation', 'coverage-gap', `branch-${activeBranch.id}-invalid-order`)}
                    branches={conditionalBranchesList}
                    activeIndex={clampedBranchIndex}
                    onActiveIndexChange={setActiveBranchIndex}
                    onBranchChange={updateConditionalBranch}
                    onAdd={addConditionalBranch}
                    onRemove={removeConditionalBranch}
                    onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                  >
                    {/* The item list is nested INSIDE this same envelope when a condition is
                        active — extends the border down to contain the equation, the condition
                        carousel/threshold editor, AND the answers together as one visibly
                        connected structure, exactly like MCQ's option list nests inside its own
                        branch envelope. It always shows/edits the SAME shared items (text, media)
                        no matter which condition is selected above; only their ARRANGEMENT
                        changes (that condition's own order, or the base order otherwise) — there
                        is deliberately only one editable item list, never a second read-only one
                        per condition. */}
                    {/* No box — see the carousel's own border. The per-branch order error it used
                        to carry now colours that border instead. */}
                    {itemListJsx}
                    {addItemButton}
                  </BranchConditionCarousel>
                )}
              </div>
            )}

            {!(isConditionalEnabled && activeBranch) && (
              /* The scenario's own box — the carousel's shape with a variant in place of an
                 equation, and nothing at all when this block follows nothing. Without it a
                 teacher could link and then never reach scenario 2 to author its order. */
              <ScenarioSetupFrame
                scenario={scenario.activeScenario}
                name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
                pager={scenario.pager}
                unreviewedCount={scenario.unreviewedCount}
                onDismissUnreviewed={scenario.dismissUnreviewed}
              >
                {itemListJsx}
                {addItemButton}
              </ScenarioSetupFrame>
            )}

            {atCircularItemCap && (
              <p id='ordering-circular-item-cap-reached' className='text-xs italic text-ws-muted'>{t('blocks.ordering.circularMaxItemsReached')}</p>
            )}
            </DrivenRowFrame>
          </div>
      }
    />
  );
}

// ─── Sortable feedback card (shared with the targeted-feedback list) ──────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId, animateLayoutChanges: disableAnimateLayoutChanges });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}
